import { encodePath, encodeUriQuery } from './util';
/*
 * Constructs urls for vizportal site pages, given the site name and query string.
 *
 * Example usage:
 *   If the SiteUrls was created with these parameters:
 *     const siteUrls = new SiteUrls({ siteUrlName: 'mysite', queryString: 'foo=bar' });
 *   It will return URLs in this format:
 *     siteUrls.projectUrl(12);    // returns '/?foo=bar#/site/mysite/projects/12'
 *
 * There are three categories of functions on SiteUrls:
 * - fooUrl (public): returns a full URL with query string and the path in the hash
 * - fooPath (public): returns just the path that goes after the #
 * - fooPart (private): returns just a part of the path; these functions are private and static
 *
 * For bare links (not uses of react-router Link), the fooUrl functions should be used,
 * so the query string is preserved.
 * When using react-router, the fooPath functions should be used,
 * either for matching in Route or linking with Link.
 */

export type DataAlertDeviceType = 'default' | 'desktop' | 'phone' | 'tablet';

export namespace CatalogUrlParamKeys {
  export const column = 'column';
  export const embeddedDataSource = 'embeddedDataSource';
  export const field = 'field';
  export const order = 'order';
  export const outputStep = 'outputStep';
  export const search = 'search';
  export const table = 'table';
}

function tagParam(tag: string): string {
  return 'tag=' + encodeUriQuery(tag);
}

function deviceTypeParam(deviceType: string): string {
  return ':device=' + encodeUriQuery(deviceType);
}

export class SiteUrls {
  private readonly queryString: string;
  private readonly sitePrefix: string;
  private readonly vizClientSitePrefix: string;

  public constructor(config: { siteUrlName: string; queryString?: string }) {
    if (config.queryString && config.queryString.length > 0) {
      // Prepend a '?' if missing
      this.queryString =
        (config.queryString[0] === '?' ? '' : '?') + config.queryString;
    } else {
      this.queryString = '';
    }

    this.sitePrefix = SiteUrls.makeSitePrefix(config.siteUrlName);
    this.vizClientSitePrefix = SiteUrls.makeVizClientSitePrefix(
      config.siteUrlName
    );
  }

  // Returns a copy of SiteUrls modified for a different siteUrlName
  public forSiteName(siteName: string): SiteUrls {
    return new SiteUrls({
      siteUrlName: siteName,
      queryString: this.queryString,
    });
  }

  // The full url is prefixed by the query string and puts the path in the hash.
  private fullUrl(path: string): string {
    return `/${this.queryString}#${path}`;
  }

  // A site path is a path starting with /site/<siteUrlName>
  private sitePath(path: string): string {
    return `${this.sitePrefix}${path}`;
  }
  private static makeSitePrefix(siteUrlName: string): string {
    return siteUrlName && siteUrlName.length > 0 ? `/site/${siteUrlName}` : '';
  }

  public defaultUrl(): string {
    return this.fullUrl(this.defaultPath());
  }

  public defaultPath(): string {
    return this.sitePath('/');
  }

  // Data Catalog

  public catalogUrl(): string {
    return this.fullUrl(this.catalogPath());
  }
  public catalogPath(): string {
    return this.sitePath(SiteUrls.catalogPart());
  }
  private static catalogPart(): string {
    return '/catalog';
  }

  // Data Catalog shared parts

  private static columnsPart(): string {
    return '/columns';
  }
  private static dashboardsPart(): string {
    return '/dashboards';
  }
  private static databasesPart(): string {
    return '/databases';
  }
  private static downstreamPart(): string {
    return '/downstream';
  }
  private static fieldsPart(): string {
    return '/fields';
  }
  private static lineagePart(): string {
    return '/lineage';
  }
  private static lineageMetricsPart(): string {
    return '/metrics';
  }
  private static ownersPart(): string {
    return '/owners';
  }
  private static sheetsPart(): string {
    return '/sheets';
  }
  private static tablesPart(): string {
    return '/tables';
  }
  private static upstreamPart(): string {
    return '/upstream';
  }

  private static filesPart(): string {
    return '/files';
  }

  private static externalAssetsShowAllPart(): string {
    return '/externalassets';
  }

  // Table

  public catalogTablesUrl(): string {
    return this.fullUrl(this.catalogTablesPath());
  }
  public catalogTablesPath(): string {
    return this.sitePath(SiteUrls.catalogTablesPart());
  }
  private static catalogTablesPart(): string {
    return `${SiteUrls.catalogPart()}${SiteUrls.tablesPart()}`;
  }

  public catalogTableUrl(tableId: string): string {
    return this.fullUrl(this.catalogTablePath(tableId));
  }
  public catalogTablePath(tableId: string): string {
    return this.sitePath(SiteUrls.catalogTablePart(tableId));
  }
  private static catalogTablePart(tableId: string): string {
    return `${SiteUrls.catalogTablesPart()}/${tableId}`;
  }

  public catalogTableColumnsUrl(tableId: string): string {
    return this.fullUrl(this.catalogTableColumnsPath(tableId));
  }
  public catalogTableColumnsPath(tableId: string): string {
    return this.sitePath(SiteUrls.catalogTableColumnsPart(tableId));
  }
  private static catalogTableColumnsPart(tableId: string): string {
    return `${SiteUrls.catalogTablePart(tableId)}${SiteUrls.columnsPart()}`;
  }

  public catalogTableColumnUrl(tableId: string, columnId: string): string {
    return this.fullUrl(this.catalogTableColumnPath(tableId, columnId));
  }
  public catalogTableColumnPath(tableId: string, columnId: string): string {
    return this.sitePath(SiteUrls.catalogTableColumnPart(tableId, columnId));
  }
  private static catalogTableColumnPart(
    tableId: string,
    columnId: string
  ): string {
    return `${SiteUrls.catalogTablePart(
      tableId
    )}${SiteUrls.columnsPart()}/${columnId}`;
  }

  public catalogTableUpstreamDatabasesUrl(tableId: string): string {
    return this.fullUrl(this.catalogTableUpstreamDatabasesPath(tableId));
  }
  public catalogTableUpstreamDatabasesPath(tableId: string): string {
    return this.sitePath(SiteUrls.catalogTableUpstreamDatabasesPart(tableId));
  }
  private static catalogTableUpstreamDatabasesPart(tableId: string): string {
    return `${SiteUrls.catalogTablePart(
      tableId
    )}${SiteUrls.upstreamPart()}${SiteUrls.databasesPart()}`;
  }

  public catalogTableUpstreamTablesUrl(tableId: string): string {
    return this.fullUrl(this.catalogTableUpstreamTablesPath(tableId));
  }
  public catalogTableUpstreamTablesPath(tableId: string): string {
    return this.sitePath(SiteUrls.catalogTableUpstreamTablesPart(tableId));
  }
  private static catalogTableUpstreamTablesPart(tableId: string): string {
    return `${SiteUrls.catalogTablePart(
      tableId
    )}${SiteUrls.upstreamPart()}${SiteUrls.tablesPart()}`;
  }

  public catalogTableUpstreamFlowsUrl(tableId: string): string {
    return this.fullUrl(this.catalogTableUpstreamFlowsPath(tableId));
  }
  public catalogTableUpstreamFlowsPath(tableId: string): string {
    return this.sitePath(SiteUrls.catalogTableUpstreamFlowsPart(tableId));
  }
  private static catalogTableUpstreamFlowsPart(tableId: string): string {
    return `${SiteUrls.catalogTablePart(
      tableId
    )}${SiteUrls.upstreamPart()}${SiteUrls.flowsPart()}`;
  }

  public catalogTableUpstreamDatasourcesUrl(tableId: string): string {
    return this.fullUrl(this.catalogTableUpstreamDatasourcesPath(tableId));
  }
  public catalogTableUpstreamDatasourcesPath(tableId: string): string {
    return this.sitePath(SiteUrls.catalogTableUpstreamDatasourcesPart(tableId));
  }
  private static catalogTableUpstreamDatasourcesPart(tableId: string): string {
    return `${SiteUrls.catalogTablePart(
      tableId
    )}${SiteUrls.upstreamPart()}${SiteUrls.datasourcesPart()}`;
  }

  public catalogTableDownstreamDatabasesUrl(tableId: string): string {
    return this.fullUrl(this.catalogTableDownstreamDatabasesPath(tableId));
  }
  public catalogTableDownstreamDatabasesPath(tableId: string): string {
    return this.sitePath(SiteUrls.catalogTableDownstreamDatabasesPart(tableId));
  }
  private static catalogTableDownstreamDatabasesPart(tableId: string): string {
    return `${SiteUrls.catalogTablePart(
      tableId
    )}${SiteUrls.downstreamPart()}${SiteUrls.databasesPart()}`;
  }

  public catalogTableDownstreamTablesUrl(tableId: string): string {
    return this.fullUrl(this.catalogTableDownstreamTablesPath(tableId));
  }
  public catalogTableDownstreamTablesPath(tableId: string): string {
    return this.sitePath(SiteUrls.catalogTableDownstreamTablesPart(tableId));
  }
  private static catalogTableDownstreamTablesPart(tableId: string): string {
    return `${SiteUrls.catalogTablePart(
      tableId
    )}${SiteUrls.downstreamPart()}${SiteUrls.tablesPart()}`;
  }

  public catalogTableDownstreamVirtualConnectionsUrl(tableId: string): string {
    return this.fullUrl(this.catalogTableDownstreamVirtualConnectionsPath(tableId));
  }
  public catalogTableDownstreamVirtualConnectionsPath(tableId: string): string {
    return this.sitePath(SiteUrls.catalogTableDownstreamVirtualConnectionsPart(tableId));
  }
  private static catalogTableDownstreamVirtualConnectionsPart(tableId: string): string {
    return `${SiteUrls.catalogTablePart(
      tableId
    )}${SiteUrls.downstreamPart()}${SiteUrls.virtualConnectionsPart()}`;
  }

  public catalogTableDownstreamVirtualConnectionTablesUrl(tableId: string): string {
    return this.fullUrl(this.catalogTableDownstreamVirtualConnectionTablesPath(tableId));
  }
  public catalogTableDownstreamVirtualConnectionTablesPath(tableId: string): string {
    return this.sitePath(SiteUrls.catalogTableDownstreamVirtualConnectionTablesPart(tableId));
  }
  private static catalogTableDownstreamVirtualConnectionTablesPart(tableId: string): string {
    return `${SiteUrls.catalogTablePart(
      tableId
    )}${SiteUrls.downstreamPart()}${SiteUrls.virtualConnectionTablesPart()}`;
  }

  public catalogTableDownstreamFlowsUrl(tableId: string): string {
    return this.fullUrl(this.catalogTableDownstreamFlowsPath(tableId));
  }
  public catalogTableDownstreamFlowsPath(tableId: string): string {
    return this.sitePath(SiteUrls.catalogTableDownstreamFlowsPart(tableId));
  }
  private static catalogTableDownstreamFlowsPart(tableId: string): string {
    return `${SiteUrls.catalogTablePart(
      tableId
    )}${SiteUrls.downstreamPart()}${SiteUrls.flowsPart()}`;
  }

  public catalogTableDownstreamDatasourcesUrl(tableId: string): string {
    return this.fullUrl(this.catalogTableDownstreamDatasourcesPath(tableId));
  }
  public catalogTableDownstreamDatasourcesPath(tableId: string): string {
    return this.sitePath(
      SiteUrls.catalogTableDownstreamDatasourcesPart(tableId)
    );
  }
  private static catalogTableDownstreamDatasourcesPart(
    tableId: string
  ): string {
    return `${SiteUrls.catalogTablePart(
      tableId
    )}${SiteUrls.downstreamPart()}${SiteUrls.datasourcesPart()}`;
  }

  public catalogTableDownstreamLensesUrl(tableId: string): string {
    return this.fullUrl(this.catalogTableDownstreamLensesPath(tableId));
  }
  public catalogTableDownstreamLensesPath(tableId: string): string {
    return this.sitePath(
      SiteUrls.catalogTableDownstreamLensesPart(tableId)
    );
  }
  private static catalogTableDownstreamLensesPart(
    tableId: string
  ): string {
    return `${SiteUrls.catalogTablePart(
      tableId
    )}${SiteUrls.downstreamPart()}${SiteUrls.lensesPart()}`;
  }

  public catalogTableDownstreamWorkbooksUrl(tableId: string): string {
    return this.fullUrl(this.catalogTableDownstreamWorkbooksPath(tableId));
  }
  public catalogTableDownstreamWorkbooksPath(tableId: string): string {
    return this.sitePath(SiteUrls.catalogTableDownstreamWorkbooksPart(tableId));
  }
  private static catalogTableDownstreamWorkbooksPart(tableId: string): string {
    return `${SiteUrls.catalogTablePart(
      tableId
    )}${SiteUrls.downstreamPart()}${SiteUrls.workbooksPart()}`;
  }

  public catalogTableDownstreamDashboardsUrl(tableId: string): string {
    return this.fullUrl(this.catalogTableDownstreamDashboardsPath(tableId));
  }
  public catalogTableDownstreamDashboardsPath(tableId: string): string {
    return this.sitePath(
      SiteUrls.catalogTableDownstreamDashboardsPart(tableId)
    );
  }
  private static catalogTableDownstreamDashboardsPart(tableId: string): string {
    return `${SiteUrls.catalogTablePart(
      tableId
    )}${SiteUrls.downstreamPart()}${SiteUrls.dashboardsPart()}`;
  }

  public catalogTableDownstreamSheetsUrl(tableId: string): string {
    return this.fullUrl(this.catalogTableDownstreamSheetsPath(tableId));
  }
  public catalogTableDownstreamSheetsPath(tableId: string): string {
    return this.sitePath(SiteUrls.catalogTableDownstreamSheetsPart(tableId));
  }
  private static catalogTableDownstreamSheetsPart(tableId: string): string {
    return `${SiteUrls.catalogTablePart(
      tableId
    )}${SiteUrls.downstreamPart()}${SiteUrls.sheetsPart()}`;
  }

  public catalogTableDownstreamMetricsUrl(tableId: string): string {
    return this.fullUrl(this.catalogTableDownstreamMetricsPath(tableId));
  }
  public catalogTableDownstreamMetricsPath(tableId: string): string {
    return this.sitePath(SiteUrls.catalogTableDownstreamMetricsPart(tableId));
  }
  private static catalogTableDownstreamMetricsPart(tableId: string): string {
    return `${SiteUrls.catalogTablePart(
      tableId
    )}${SiteUrls.downstreamPart()}${SiteUrls.lineageMetricsPart()}`;
  }

  public catalogTableDownstreamOwnersUrl(tableId: string): string {
    return this.fullUrl(this.catalogTableDownstreamOwnersPath(tableId));
  }
  public catalogTableDownstreamOwnersPath(tableId: string): string {
    return this.sitePath(SiteUrls.catalogTableDownstreamOwnersPart(tableId));
  }
  private static catalogTableDownstreamOwnersPart(tableId: string): string {
    return `${SiteUrls.catalogTablePart(
      tableId
    )}${SiteUrls.downstreamPart()}${SiteUrls.ownersPart()}`;
  }

  // Database

  public catalogDatabasesUrl(): string {
    return this.fullUrl(this.catalogDatabasesPath());
  }
  public catalogDatabasesPath(): string {
    return this.sitePath(SiteUrls.catalogDatabasesPart());
  }
  private static catalogDatabasesPart(): string {
    return `${SiteUrls.catalogPart()}${SiteUrls.databasesPart()}`;
  }

  public catalogDatabaseUrl(databaseId: string): string {
    return this.fullUrl(this.catalogDatabasePath(databaseId));
  }
  public catalogDatabasePath(databaseId: string): string {
    return this.sitePath(SiteUrls.catalogDatabasePart(databaseId));
  }
  private static catalogDatabasePart(databaseId: string): string {
    return `${SiteUrls.catalogDatabasesPart()}/${databaseId}`;
  }

  public catalogDatabaseTablesUrl(databaseId: string): string {
    return this.fullUrl(this.catalogDatabaseTablesPath(databaseId));
  }
  public catalogDatabaseTablesPath(databaseId: string): string {
    return this.sitePath(SiteUrls.catalogDatabaseTablesPart(databaseId));
  }
  private static catalogDatabaseTablesPart(databaseId: string): string {
    return `${SiteUrls.catalogDatabasePart(
      databaseId
    )}${SiteUrls.tablesPart()}`;
  }

  public catalogDatabaseUpstreamDatabasesUrl(databaseId: string): string {
    return this.fullUrl(this.catalogDatabaseUpstreamDatabasesPath(databaseId));
  }
  public catalogDatabaseUpstreamDatabasesPath(databaseId: string): string {
    return this.sitePath(
      SiteUrls.catalogDatabaseUpstreamDatabasesPart(databaseId)
    );
  }
  private static catalogDatabaseUpstreamDatabasesPart(
    databaseId: string
  ): string {
    return `${SiteUrls.catalogDatabasePart(
      databaseId
    )}${SiteUrls.upstreamPart()}${SiteUrls.databasesPart()}`;
  }

  public catalogDatabaseUpstreamTablesUrl(databaseId: string): string {
    return this.fullUrl(this.catalogDatabaseUpstreamTablesPath(databaseId));
  }
  public catalogDatabaseUpstreamTablesPath(databaseId: string): string {
    return this.sitePath(
      SiteUrls.catalogDatabaseUpstreamTablesPart(databaseId)
    );
  }
  private static catalogDatabaseUpstreamTablesPart(databaseId: string): string {
    return `${SiteUrls.catalogDatabasePart(
      databaseId
    )}${SiteUrls.upstreamPart()}${SiteUrls.tablesPart()}`;
  }

  public catalogDatabaseUpstreamFlowsUrl(databaseId: string): string {
    return this.fullUrl(this.catalogDatabaseUpstreamFlowsPath(databaseId));
  }
  public catalogDatabaseUpstreamFlowsPath(databaseId: string): string {
    return this.sitePath(SiteUrls.catalogDatabaseUpstreamFlowsPart(databaseId));
  }
  private static catalogDatabaseUpstreamFlowsPart(databaseId: string): string {
    return `${SiteUrls.catalogDatabasePart(
      databaseId
    )}${SiteUrls.upstreamPart()}${SiteUrls.flowsPart()}`;
  }

  public catalogDatabaseUpstreamDatasourcesUrl(databaseId: string): string {
    return this.fullUrl(
      this.catalogDatabaseUpstreamDatasourcesPath(databaseId)
    );
  }
  public catalogDatabaseUpstreamDatasourcesPath(databaseId: string): string {
    return this.sitePath(
      SiteUrls.catalogDatabaseUpstreamDatasourcesPart(databaseId)
    );
  }
  private static catalogDatabaseUpstreamDatasourcesPart(
    databaseId: string
  ): string {
    return `${SiteUrls.catalogDatabasePart(
      databaseId
    )}${SiteUrls.upstreamPart()}${SiteUrls.datasourcesPart()}`;
  }

  public catalogDatabaseDownstreamDatabasesUrl(databaseId: string): string {
    return this.fullUrl(
      this.catalogDatabaseDownstreamDatabasesPath(databaseId)
    );
  }
  public catalogDatabaseDownstreamDatabasesPath(databaseId: string): string {
    return this.sitePath(
      SiteUrls.catalogDatabaseDownstreamDatabasesPart(databaseId)
    );
  }
  private static catalogDatabaseDownstreamDatabasesPart(
    databaseId: string
  ): string {
    return `${SiteUrls.catalogDatabasePart(
      databaseId
    )}${SiteUrls.downstreamPart()}${SiteUrls.databasesPart()}`;
  }

  public catalogDatabaseDownstreamTablesUrl(databaseId: string): string {
    return this.fullUrl(this.catalogDatabaseDownstreamTablesPath(databaseId));
  }
  public catalogDatabaseDownstreamTablesPath(databaseId: string): string {
    return this.sitePath(
      SiteUrls.catalogDatabaseDownstreamTablesPart(databaseId)
    );
  }
  private static catalogDatabaseDownstreamTablesPart(
    databaseId: string
  ): string {
    return `${SiteUrls.catalogDatabasePart(
      databaseId
    )}${SiteUrls.downstreamPart()}${SiteUrls.tablesPart()}`;
  }

  public catalogDatabaseDownstreamVirtualConnectionsUrl(databaseId: string): string {
    return this.fullUrl(this.catalogDatabaseDownstreamVirtualConnectionsPath(databaseId));
  }
  public catalogDatabaseDownstreamVirtualConnectionsPath(databaseId: string): string {
    return this.sitePath(
      SiteUrls.catalogDatabaseDownstreamVirtualConnectionsPart(databaseId)
    );
  }
  private static catalogDatabaseDownstreamVirtualConnectionsPart(
    databaseId: string
  ): string {
    return `${SiteUrls.catalogDatabasePart(
      databaseId
    )}${SiteUrls.downstreamPart()}${SiteUrls.virtualConnectionsPart()}`;
  }

  public catalogDatabaseDownstreamVirtualConnectionTablesUrl(databaseId: string): string {
    return this.fullUrl(this.catalogDatabaseDownstreamVirtualConnectionTablesPath(databaseId));
  }
  public catalogDatabaseDownstreamVirtualConnectionTablesPath(databaseId: string): string {
    return this.sitePath(
      SiteUrls.catalogDatabaseDownstreamVirtualConnectionTablesPart(databaseId)
    );
  }
  private static catalogDatabaseDownstreamVirtualConnectionTablesPart(
    databaseId: string
  ): string {
    return `${SiteUrls.catalogDatabasePart(
      databaseId
    )}${SiteUrls.downstreamPart()}${SiteUrls.virtualConnectionTablesPart()}`;
  }

  public catalogDatabaseDownstreamFlowsUrl(databaseId: string): string {
    return this.fullUrl(this.catalogDatabaseDownstreamFlowsPath(databaseId));
  }
  public catalogDatabaseDownstreamFlowsPath(databaseId: string): string {
    return this.sitePath(
      SiteUrls.catalogDatabaseDownstreamFlowsPart(databaseId)
    );
  }
  private static catalogDatabaseDownstreamFlowsPart(
    databaseId: string
  ): string {
    return `${SiteUrls.catalogDatabasePart(
      databaseId
    )}${SiteUrls.downstreamPart()}${SiteUrls.flowsPart()}`;
  }

  public catalogDatabaseDownstreamDatasourcesUrl(databaseId: string): string {
    return this.fullUrl(
      this.catalogDatabaseDownstreamDatasourcesPath(databaseId)
    );
  }
  public catalogDatabaseDownstreamDatasourcesPath(databaseId: string): string {
    return this.sitePath(
      SiteUrls.catalogDatabaseDownstreamDatasourcesPart(databaseId)
    );
  }
  private static catalogDatabaseDownstreamDatasourcesPart(
    databaseId: string
  ): string {
    return `${SiteUrls.catalogDatabasePart(
      databaseId
    )}${SiteUrls.downstreamPart()}${SiteUrls.datasourcesPart()}`;
  }

  public catalogDatabaseDownstreamLensesUrl(databaseId: string): string {
    return this.fullUrl(
      this.catalogDatabaseDownstreamLensesPath(databaseId)
    );
  }
  public catalogDatabaseDownstreamLensesPath(databaseId: string): string {
    return this.sitePath(
      SiteUrls.catalogDatabaseDownstreamLensesPart(databaseId)
    );
  }
  private static catalogDatabaseDownstreamLensesPart(
    databaseId: string
  ): string {
    return `${SiteUrls.catalogDatabasePart(
      databaseId
    )}${SiteUrls.downstreamPart()}${SiteUrls.lensesPart()}`;
  }

  public catalogDatabaseDownstreamWorkbooksUrl(databaseId: string): string {
    return this.fullUrl(
      this.catalogDatabaseDownstreamWorkbooksPath(databaseId)
    );
  }
  public catalogDatabaseDownstreamWorkbooksPath(databaseId: string): string {
    return this.sitePath(
      SiteUrls.catalogDatabaseDownstreamWorkbooksPart(databaseId)
    );
  }
  private static catalogDatabaseDownstreamWorkbooksPart(
    databaseId: string
  ): string {
    return `${SiteUrls.catalogDatabasePart(
      databaseId
    )}${SiteUrls.downstreamPart()}${SiteUrls.workbooksPart()}`;
  }

  public catalogDatabaseDownstreamDashboardsUrl(databaseId: string): string {
    return this.fullUrl(
      this.catalogDatabaseDownstreamDashboardsPath(databaseId)
    );
  }
  public catalogDatabaseDownstreamDashboardsPath(databaseId: string): string {
    return this.sitePath(
      SiteUrls.catalogDatabaseDownstreamDashboardsPart(databaseId)
    );
  }
  private static catalogDatabaseDownstreamDashboardsPart(
    databaseId: string
  ): string {
    return `${SiteUrls.catalogDatabasePart(
      databaseId
    )}${SiteUrls.downstreamPart()}${SiteUrls.dashboardsPart()}`;
  }

  public catalogDatabaseDownstreamSheetsUrl(databaseId: string): string {
    return this.fullUrl(this.catalogDatabaseDownstreamSheetsPath(databaseId));
  }
  public catalogDatabaseDownstreamSheetsPath(databaseId: string): string {
    return this.sitePath(
      SiteUrls.catalogDatabaseDownstreamSheetsPart(databaseId)
    );
  }
  private static catalogDatabaseDownstreamSheetsPart(
    databaseId: string
  ): string {
    return `${SiteUrls.catalogDatabasePart(
      databaseId
    )}${SiteUrls.downstreamPart()}${SiteUrls.sheetsPart()}`;
  }

  public catalogDatabaseDownstreaMetricsUrl(databaseId: string): string {
    return this.fullUrl(this.catalogDatabaseDownstreamMetricsPath(databaseId));
  }
  public catalogDatabaseDownstreamMetricsPath(databaseId: string): string {
    return this.sitePath(
      SiteUrls.catalogDatabaseDownstreamMetricsPart(databaseId)
    );
  }
  private static catalogDatabaseDownstreamMetricsPart(
    databaseId: string
  ): string {
    return `${SiteUrls.catalogDatabasePart(
      databaseId
    )}${SiteUrls.downstreamPart()}${SiteUrls.lineageMetricsPart()}`;
  }

  public catalogDatabaseDownstreamOwnersUrl(databaseId: string): string {
    return this.fullUrl(this.catalogDatabaseDownstreamOwnersPath(databaseId));
  }
  public catalogDatabaseDownstreamOwnersPath(databaseId: string): string {
    return this.sitePath(
      SiteUrls.catalogDatabaseDownstreamOwnersPart(databaseId)
    );
  }
  private static catalogDatabaseDownstreamOwnersPart(
    databaseId: string
  ): string {
    return `${SiteUrls.catalogDatabasePart(
      databaseId
    )}${SiteUrls.downstreamPart()}${SiteUrls.ownersPart()}`;
  }

  public catalogDatabasesWithTagUrl(tag: string): string {
    return this.fullUrl(this.catalogDatabasesWithTagPath(tag));
  }
  public catalogDatabasesWithTagPath(tag: string): string {
    return this.sitePath(SiteUrls.catalogDatabasesWithTagPart(tag));
  }
  private static catalogDatabasesWithTagPart(tag: string): string {
    return `${SiteUrls.catalogDatabasesPart()}${SiteUrls.tagPart(tag)}`;
  }

  public catalogTablesWithTagUrl(tag: string): string {
    return this.fullUrl(this.catalogTablesWithTagPath(tag));
  }
  public catalogTablesWithTagPath(tag: string): string {
    return this.sitePath(SiteUrls.catalogTablesWithTagPart(tag));
  }
  private static catalogTablesWithTagPart(tag: string): string {
    return `${SiteUrls.catalogTablesPart()}${SiteUrls.tagPart(tag)}`;
  }

  // Explore

  public exploreUrl(): string {
    return this.fullUrl(this.explorePath());
  }
  public explorePath(): string {
    return this.sitePath(SiteUrls.explorePart());
  }
  private static explorePart(): string {
    return '/explore';
  }

  // DataRole

  public dataRolesUrl(): string {
    return this.fullUrl(this.dataRolesPath());
  }
  public dataRolesPath(): string {
    return this.sitePath(SiteUrls.dataRolesPart());
  }
  private static dataRolesPart(): string {
    return '/dataRoles';
  }

  public dataRoleUrl(roleId: string): string {
    return this.fullUrl(this.dataRolePath(roleId));
  }
  public dataRolePath(roleId: string): string {
    return this.sitePath(SiteUrls.dataRolePart(roleId));
  }
  private static dataRolePart(roleId: string): string {
    return `${SiteUrls.dataRolesPart()}/${roleId}`;
  }

  public redirectToDataRoleUrl(roleId: string): string {
    return this.fullUrl(this.redirectToDataRolePath(roleId));
  }
  public redirectToDataRolePath(roleId: string): string {
    return this.sitePath(SiteUrls.redirectToDataRolePart(roleId));
  }
  private static redirectToDataRolePart(roleId: string): string {
    return `/redirect_to_data_role/${roleId}`;
  }

  public dataRoleConnectedFlowsUrl(roleId: string): string {
    return this.fullUrl(this.dataRoleConnectedFlowsPath(roleId));
  }
  public dataRoleConnectedFlowsPath(roleId: string): string {
    return this.sitePath(SiteUrls.dataRoleConnectedFlowsPart(roleId));
  }
  private static dataRoleConnectedFlowsPart(roleId: string): string {
    return `${SiteUrls.dataRolesPart()}/${roleId}${this.connectedFlowsPart()}`;
  }
  private static connectedFlowsPart(): string {
    return '/connectedFlows';
  }

  public dataRoleConnectedDatasourcesUrl(roleId: string): string {
    return this.fullUrl(this.dataRoleConnectedDatasourcesPath(roleId));
  }
  public dataRoleConnectedDatasourcesPath(roleId: string): string {
    return this.sitePath(SiteUrls.dataRoleConnectedDatasourcesPart(roleId));
  }
  private static dataRoleConnectedDatasourcesPart(roleId: string): string {
    return `${SiteUrls.dataRolesPart()}/${roleId}${this.connectedDatasourcesPart()}`;
  }
  private static connectedDatasourcesPart(): string {
    return '/connectedDatasources';
  }

  public dataRoleDefinitionUrl(roleId: string): string {
    return this.fullUrl(this.dataRoleDefinitionPath(roleId));
  }
  public dataRoleDefinitionPath(roleId: string): string {
    return this.sitePath(SiteUrls.dataRoleDefinitionPart(roleId));
  }
  private static dataRoleDefinitionPart(roleId: string): string {
    return `${SiteUrls.dataRolesPart()}/${roleId}${this.definitionPart()}`;
  }
  private static definitionPart(): string {
    return '/definition';
  }

  // Files
  public catalogFilesUrl(): string {
    return this.fullUrl(this.catalogFilesPath());
  }
  public catalogFilesPath(): string {
    return this.sitePath(SiteUrls.catalogFilesPart());
  }
  private static catalogFilesPart(): string {
    return `${SiteUrls.catalogPart()}${SiteUrls.filesPart()}`;
  }
  public filesUrl(): string {
    return this.fullUrl(this.filesPath());
  }
  public filesPath(): string {
    return this.sitePath(SiteUrls.filesPart());
  }

  // Show all external assets
  public catalogExternalAssetsShowAllUrl(): string {
    return this.fullUrl(this.catalogExternalAssetsShowAllPath());
  }
  public catalogExternalAssetsShowAllPath(): string {
    return this.sitePath(SiteUrls.catalogExternalAssetsShowAllPart());
  }
  private static catalogExternalAssetsShowAllPart(): string {
    return `${SiteUrls.catalogPart()}${SiteUrls.externalAssetsShowAllPart()}`;
  }
  public externalAssetsShowAllUrl(): string {
    return this.fullUrl(this.externalAssetsShowAllPath());
  }
  public externalAssetsShowAllPath(): string {
    return this.sitePath(SiteUrls.externalAssetsShowAllPart());
  }

  // Lenses

  public lensesUrl(): string {
    return this.fullUrl(this.lensesPath());
  }
  public lensesPath(): string {
    return this.sitePath(SiteUrls.lensesPart());
  }
  private static lensesPart(): string {
    return '/lenses';
  }

  public lensUrl(lensId: string): string {
    return this.fullUrl(this.lensPath(lensId));
  }
  public lensPath(lensId: string): string {
    return this.sitePath(SiteUrls.lensPart(lensId));
  }
  private static lensPart(lensId: string): string {
    return `${SiteUrls.lensesPart()}/${lensId}`;
  }

  public lensUsageAnalyticsUrl(lensId: string): string {
    return this.fullUrl(this.lensUsageAnalyticsPath(lensId));
  }
  public lensUsageAnalyticsPath(lensId: string): string {
    return this.sitePath(SiteUrls.lensUsageAnalyticsPart(lensId));
  }
  private static lensUsageAnalyticsPart(lensId: string): string {
    return `${SiteUrls.lensPart(lensId)}${SiteUrls.usageAnalyticsPart()}`;
  }
  private static usageAnalyticsPart(): string {
    return '/usageAnalytics';
  }

  public lensLineageUrl(lensId: string): string {
    return this.fullUrl(this.lensLineagePath(lensId));
  }
  public lensLineagePath(lensId: string): string {
    return this.sitePath(SiteUrls.lensLineagePart(lensId));
  }
  private static lensLineagePart(lensId: string): string {
    return `${SiteUrls.lensPart(lensId)}${SiteUrls.lineagePart()}`;
  }

  public lensLineageFieldsUrl(lensId: string): string {
    return this.fullUrl(this.lensLineageFieldsPath(lensId));
  }
  public lensLineageFieldsPath(lensId: string): string {
    return this.sitePath(SiteUrls.lensLineageFieldsPart(lensId));
  }
  private static lensLineageFieldsPart(lensId: string): string {
    return `${SiteUrls.lensLineagePart(lensId)}${SiteUrls.fieldsPart()}`;
  }

  public lensLineageFieldUrl(
    lensId: string,
    fieldId: string
  ): string {
    return this.fullUrl(this.lensLineageFieldPath(lensId, fieldId));
  }
  public lensLineageFieldPath(
    lensId: string,
    fieldId: string
  ): string {
    return this.sitePath(
      SiteUrls.lensLineageFieldPart(lensId, fieldId)
    );
  }
  private static lensLineageFieldPart(
    lensId: string,
    fieldId: string
  ): string {
    return `${SiteUrls.lensLineagePart(
      lensId
    )}${SiteUrls.fieldsPart()}/${fieldId}`;
  }

  public lensLineageUpstreamDatabasesUrl(lensId: string): string {
    return this.fullUrl(
      this.lensLineageUpstreamDatabasesPath(lensId)
    );
  }
  public lensLineageUpstreamDatabasesPath(lensId: string): string {
    return this.sitePath(
      SiteUrls.lensLineageUpstreamDatabasesPart(lensId)
    );
  }
  private static lensLineageUpstreamDatabasesPart(
    lensId: string
  ): string {
    return `${SiteUrls.lensLineagePart(
      lensId
    )}${SiteUrls.upstreamPart()}${SiteUrls.databasesPart()}`;
  }

  public lensLineageUpstreamTablesUrl(lensId: string): string {
    return this.fullUrl(this.lensLineageUpstreamTablesPath(lensId));
  }
  public lensLineageUpstreamTablesPath(lensId: string): string {
    return this.sitePath(
      SiteUrls.lensLineageUpstreamTablesPart(lensId)
    );
  }
  private static lensLineageUpstreamTablesPart(
    lensId: string
  ): string {
    return `${SiteUrls.lensLineagePart(
      lensId
    )}${SiteUrls.upstreamPart()}${SiteUrls.tablesPart()}`;
  }

  public lensLineageUpstreamVirtualConnectionsUrl(lensId: string): string {
    return this.fullUrl(this.lensLineageUpstreamVirtualConnectionsPath(lensId));
  }
  public lensLineageUpstreamVirtualConnectionsPath(lensId: string): string {
    return this.sitePath(
      SiteUrls.lensLineageUpstreamVirtualConnectionsPart(lensId)
    );
  }
  private static lensLineageUpstreamVirtualConnectionsPart(
    lensId: string
  ): string {
    return `${SiteUrls.lensLineagePart(
      lensId
    )}${SiteUrls.upstreamPart()}${SiteUrls.virtualConnectionsPart()}`;
  }

  public lensLineageUpstreamVirtualConnectionTablesUrl(lensId: string): string {
    return this.fullUrl(this.lensLineageUpstreamVirtualConnectionTablesPath(lensId));
  }
  public lensLineageUpstreamVirtualConnectionTablesPath(lensId: string): string {
    return this.sitePath(
      SiteUrls.lensLineageUpstreamVirtualConnectionTablesPart(lensId)
    );
  }
  private static lensLineageUpstreamVirtualConnectionTablesPart(
    lensId: string
  ): string {
    return `${SiteUrls.lensLineagePart(
      lensId
    )}${SiteUrls.upstreamPart()}${SiteUrls.virtualConnectionTablesPart()}`;
  }

  public lensLineageUpstreamFlowsUrl(lensId: string): string {
    return this.fullUrl(this.lensLineageUpstreamFlowsPath(lensId));
  }
  public lensLineageUpstreamFlowsPath(lensId: string): string {
    return this.sitePath(
      SiteUrls.lensLineageUpstreamFlowsPart(lensId)
    );
  }
  private static lensLineageUpstreamFlowsPart(
    lensId: string
  ): string {
    return `${SiteUrls.lensLineagePart(
      lensId
    )}${SiteUrls.upstreamPart()}${SiteUrls.flowsPart()}`;
  }

  public lensLineageUpstreamDatasourcesUrl(lensId: string): string {
    return this.fullUrl(
      this.lensLineageUpstreamDatasourcesPath(lensId)
    );
  }
  public lensLineageUpstreamDatasourcesPath(
    lensId: string
  ): string {
    return this.sitePath(
      SiteUrls.lensLineageUpstreamDatasourcesPart(lensId)
    );
  }
  private static lensLineageUpstreamDatasourcesPart(
    lensId: string
  ): string {
    return `${SiteUrls.lensLineagePart(
      lensId
    )}${SiteUrls.upstreamPart()}${SiteUrls.datasourcesPart()}`;
  }

  public lensLineageDownstreamWorkbooksUrl(lensId: string): string {
    return this.fullUrl(
      this.lensLineageDownstreamWorkbooksPath(lensId)
    );
  }
  public lensLineageDownstreamWorkbooksPath(
    lensId: string
  ): string {
    return this.sitePath(
      SiteUrls.lensLineageDownstreamWorkbooksPart(lensId)
    );
  }
  private static lensLineageDownstreamWorkbooksPart(
    lensId: string
  ): string {
    return `${SiteUrls.lensLineagePart(
      lensId
    )}${SiteUrls.downstreamPart()}${SiteUrls.workbooksPart()}`;
  }

  public lensLineageDownstreamDashboardsUrl(
    lensId: string
  ): string {
    return this.fullUrl(
      this.lensLineageDownstreamDashboardsPath(lensId)
    );
  }
  public lensLineageDownstreamDashboardsPath(
    lensId: string
  ): string {
    return this.sitePath(
      SiteUrls.lensLineageDownstreamDashboardsPart(lensId)
    );
  }
  private static lensLineageDownstreamDashboardsPart(
    lensId: string
  ): string {
    return `${SiteUrls.lensLineagePart(
      lensId
    )}${SiteUrls.downstreamPart()}${SiteUrls.dashboardsPart()}`;
  }

  public lensLineageDownstreamMetricsUrl(lensId: string): string {
    return this.fullUrl(
      this.lensLineageDownstreamMetricsPath(lensId)
    );
  }
  public lensLineageDownstreamMetricsPath(
    lensId: string
  ): string {
    return this.sitePath(
      SiteUrls.lensLineageDownstreamMetricsPart(lensId)
    );
  }
  private static lensLineageDownstreamMetricsPart(
    lensId: string
  ): string {
    return `${SiteUrls.lensLineagePart(
      lensId
    )}${SiteUrls.downstreamPart()}${SiteUrls.lineageMetricsPart()}`;
  }

  public lensLineageDownstreamOwnersUrl(lensId: string): string {
    return this.fullUrl(
      this.lensLineageDownstreamOwnersPath(lensId)
    );
  }
  public lensLineageDownstreamOwnersPath(lensId: string): string {
    return this.sitePath(
      SiteUrls.lensLineageDownstreamOwnersPart(lensId)
    );
  }
  private static lensLineageDownstreamOwnersPart(
    lensId: string
  ): string {
    return `${SiteUrls.lensLineagePart(
      lensId
    )}${SiteUrls.downstreamPart()}${SiteUrls.ownersPart()}`;
  }
  // Flows

  public flowsUrl(): string {
    return this.fullUrl(this.flowsPath());
  }
  public flowsPath(): string {
    return this.sitePath(SiteUrls.flowsPart());
  }
  private static flowsPart(): string {
    return '/flows';
  }

  public flowUrl(flowId: string): string {
    return this.fullUrl(this.flowPath(flowId));
  }
  public flowPath(flowId: string): string {
    return this.sitePath(SiteUrls.flowPart(flowId));
  }
  private static flowPart(flowId: string): string {
    return `${SiteUrls.flowsPart()}/${flowId}`;
  }

  // TODO: remove in favor of redirectToFlowUrl?
  public favoriteFlowUrl(flowId: string): string {
    return this.redirectToFlowUrl(flowId);
  }

  public redirectToFlowUrl(flowId: string): string {
    return this.fullUrl(this.redirectToFlowPath(flowId));
  }
  public redirectToFlowPath(flowId: string): string {
    return this.sitePath(SiteUrls.redirectToFlowPart(flowId));
  }
  private static redirectToFlowPart(flowId: string): string {
    return `/redirect_to_flow/${flowId}`;
  }

  public flowsWithTagUrl(tag: string): string {
    return this.fullUrl(this.flowsWithTagPath(tag));
  }
  public flowsWithTagPath(tag: string): string {
    return this.sitePath(SiteUrls.flowsWithTagPart(tag));
  }
  private static flowsWithTagPart(tag: string): string {
    return `${SiteUrls.flowsPart()}${SiteUrls.tagPart(tag)}`;
  }
  private static tagPart(tag: string): string {
    return `?${tagParam(tag)}`;
  }

  public flowConnectionsUrl(flowId: string): string {
    return this.fullUrl(this.flowConnectionsPath(flowId));
  }
  public flowConnectionsPath(flowId: string): string {
    return this.sitePath(SiteUrls.flowConnectionsPart(flowId));
  }
  private static flowConnectionsPart(flowId: string): string {
    return `${SiteUrls.flowPart(flowId)}${SiteUrls.connectionsPart()}`;
  }

  // Used for search url as well
  private static connectionsPart(): string {
    return '/connections';
  }

  public flowOverviewUrl(flowId: string): string {
    return this.fullUrl(this.flowOverviewPath(flowId));
  }
  public flowOverviewPath(flowId: string): string {
    return this.sitePath(SiteUrls.flowOverviewPart(flowId));
  }
  private static flowOverviewPart(flowId: string): string {
    return `${SiteUrls.flowPart(flowId)}${SiteUrls.overviewPart()}`;
  }
  private static overviewPart(): string {
    return '/overview';
  }

  public flowRunHistoryUrl(flowId: string): string {
    return this.fullUrl(this.flowRunHistoryPath(flowId));
  }
  public flowRunHistoryPath(flowId: string): string {
    return this.sitePath(SiteUrls.flowRunHistoryPart(flowId));
  }
  private static flowRunHistoryPart(flowId: string): string {
    return `${SiteUrls.flowPart(flowId)}${SiteUrls.runHistoryPart()}`;
  }
  private static runHistoryPart(): string {
    return '/runHistory';
  }

  public flowSubscriptionsUrl(flowId: string): string {
    return this.fullUrl(this.flowSubscriptionsPath(flowId));
  }
  public flowSubscriptionsPath(flowId: string): string {
    return this.sitePath(SiteUrls.flowSubscriptionsPart(flowId));
  }
  private static flowSubscriptionsPart(flowId: string): string {
    return `${SiteUrls.flowPart(flowId)}${SiteUrls.subscriptionsPart()}`;
  }

  public flowScheduledTasksUrl(flowId: string): string {
    return this.fullUrl(this.flowScheduledTasksPath(flowId));
  }
  public flowScheduledTasksPath(flowId: string): string {
    return this.sitePath(SiteUrls.flowScheduledTasksPart(flowId));
  }
  private static flowScheduledTasksPart(flowId: string): string {
    return `${SiteUrls.flowPart(flowId)}${SiteUrls.scheduledTasksPart()}`;
  }
  private static scheduledTasksPart(): string {
    return '/scheduledTasks';
  }

  public flowLineageUrl(flowId: string): string {
    return this.fullUrl(this.flowLineagePath(flowId));
  }
  public flowLineagePath(flowId: string): string {
    return this.sitePath(SiteUrls.flowLineagePart(flowId));
  }
  private static flowLineagePart(flowId: string): string {
    return `${SiteUrls.flowPart(flowId)}${SiteUrls.lineagePart()}`;
  }

  public flowLineageFieldsUrl(flowId: string): string {
    return this.fullUrl(this.flowLineageFieldsPath(flowId));
  }
  public flowLineageFieldsPath(flowId: string): string {
    return this.sitePath(SiteUrls.flowLineageFieldsPart(flowId));
  }
  private static flowLineageFieldsPart(flowId: string): string {
    return `${SiteUrls.flowLineagePart(flowId)}${SiteUrls.fieldsPart()}`;
  }

  public flowLineageFieldUrl(flowId: string, fieldId: string): string {
    return this.fullUrl(this.flowLineageFieldPath(flowId, fieldId));
  }
  public flowLineageFieldPath(flowId: string, fieldId: string): string {
    return this.sitePath(SiteUrls.flowLineageFieldPart(flowId, fieldId));
  }
  private static flowLineageFieldPart(flowId: string, fieldId: string): string {
    return `${SiteUrls.flowLineagePart(
      flowId
    )}${SiteUrls.fieldsPart()}/${fieldId}`;
  }

  public flowLineageUpstreamDatabasesUrl(flowId: string): string {
    return this.fullUrl(this.flowLineageUpstreamDatabasesPath(flowId));
  }
  public flowLineageUpstreamDatabasesPath(flowId: string): string {
    return this.sitePath(SiteUrls.flowLineageUpstreamDatabasesPart(flowId));
  }
  private static flowLineageUpstreamDatabasesPart(flowId: string): string {
    return `${SiteUrls.flowLineagePart(
      flowId
    )}${SiteUrls.upstreamPart()}${SiteUrls.databasesPart()}`;
  }

  public flowLineageUpstreamTablesUrl(flowId: string): string {
    return this.fullUrl(this.flowLineageUpstreamTablesPath(flowId));
  }
  public flowLineageUpstreamTablesPath(flowId: string): string {
    return this.sitePath(SiteUrls.flowLineageUpstreamTablesPart(flowId));
  }
  private static flowLineageUpstreamTablesPart(flowId: string): string {
    return `${SiteUrls.flowLineagePart(
      flowId
    )}${SiteUrls.upstreamPart()}${SiteUrls.tablesPart()}`;
  }

  public flowLineageUpstreamVirtualConnectionsUrl(flowId: string): string {
    return this.fullUrl(this.flowLineageUpstreamVirtualConnectionsPath(flowId));
  }
  public flowLineageUpstreamVirtualConnectionsPath(flowId: string): string {
    return this.sitePath(SiteUrls.flowLineageUpstreamVirtualConnectionsPart(flowId));
  }
  private static flowLineageUpstreamVirtualConnectionsPart(flowId: string): string {
    return `${SiteUrls.flowLineagePart(
      flowId
    )}${SiteUrls.upstreamPart()}${SiteUrls.virtualConnectionsPart()}`;
  }

  public flowLineageUpstreamVirtualConnectionTablesUrl(flowId: string): string {
    return this.fullUrl(this.flowLineageUpstreamVirtualConnectionTablesPath(flowId));
  }
  public flowLineageUpstreamVirtualConnectionTablesPath(flowId: string): string {
    return this.sitePath(SiteUrls.flowLineageUpstreamVirtualConnectionTablesPart(flowId));
  }
  private static flowLineageUpstreamVirtualConnectionTablesPart(flowId: string): string {
    return `${SiteUrls.flowLineagePart(
      flowId
    )}${SiteUrls.upstreamPart()}${SiteUrls.virtualConnectionTablesPart()}`;
  }

  public flowLineageUpstreamFlowsUrl(flowId: string): string {
    return this.fullUrl(this.flowLineageUpstreamFlowsPath(flowId));
  }
  public flowLineageUpstreamFlowsPath(flowId: string): string {
    return this.sitePath(SiteUrls.flowLineageUpstreamFlowsPart(flowId));
  }
  private static flowLineageUpstreamFlowsPart(flowId: string): string {
    return `${SiteUrls.flowLineagePart(
      flowId
    )}${SiteUrls.upstreamPart()}${SiteUrls.flowsPart()}`;
  }

  public flowLineageUpstreamDatasourcesUrl(flowId: string): string {
    return this.fullUrl(this.flowLineageUpstreamDatasourcesPath(flowId));
  }
  public flowLineageUpstreamDatasourcesPath(flowId: string): string {
    return this.sitePath(SiteUrls.flowLineageUpstreamDatasourcesPart(flowId));
  }
  private static flowLineageUpstreamDatasourcesPart(flowId: string): string {
    return `${SiteUrls.flowLineagePart(
      flowId
    )}${SiteUrls.upstreamPart()}${SiteUrls.datasourcesPart()}`;
  }

  public flowLineageDownstreamDatabasesUrl(flowId: string): string {
    return this.fullUrl(this.flowLineageDownstreamDatabasesPath(flowId));
  }
  public flowLineageDownstreamDatabasesPath(flowId: string): string {
    return this.sitePath(SiteUrls.flowLineageDownstreamDatabasesPart(flowId));
  }
  private static flowLineageDownstreamDatabasesPart(flowId: string): string {
    return `${SiteUrls.flowLineagePart(
      flowId
    )}${SiteUrls.downstreamPart()}${SiteUrls.databasesPart()}`;
  }

  public flowLineageDownstreamTablesUrl(flowId: string): string {
    return this.fullUrl(this.flowLineageDownstreamTablesPath(flowId));
  }
  public flowLineageDownstreamTablesPath(flowId: string): string {
    return this.sitePath(SiteUrls.flowLineageDownstreamTablesPart(flowId));
  }
  private static flowLineageDownstreamTablesPart(flowId: string): string {
    return `${SiteUrls.flowLineagePart(
      flowId
    )}${SiteUrls.downstreamPart()}${SiteUrls.tablesPart()}`;
  }

  public flowLineageDownstreamFlowsUrl(flowId: string): string {
    return this.fullUrl(this.flowLineageDownstreamFlowsPath(flowId));
  }
  public flowLineageDownstreamFlowsPath(flowId: string): string {
    return this.sitePath(SiteUrls.flowLineageDownstreamFlowsPart(flowId));
  }
  private static flowLineageDownstreamFlowsPart(flowId: string): string {
    return `${SiteUrls.flowLineagePart(
      flowId
    )}${SiteUrls.downstreamPart()}${SiteUrls.flowsPart()}`;
  }

  public flowLineageDownstreamDatasourcesUrl(flowId: string): string {
    return this.fullUrl(this.flowLineageDownstreamDatasourcesPath(flowId));
  }
  public flowLineageDownstreamDatasourcesPath(flowId: string): string {
    return this.sitePath(SiteUrls.flowLineageDownstreamDatasourcesPart(flowId));
  }
  private static flowLineageDownstreamDatasourcesPart(flowId: string): string {
    return `${SiteUrls.flowLineagePart(
      flowId
    )}${SiteUrls.downstreamPart()}${SiteUrls.datasourcesPart()}`;
  }

  public flowLineageDownstreamLensesUrl(flowId: string): string {
    return this.fullUrl(this.flowLineageDownstreamLensesPath(flowId));
  }
  public flowLineageDownstreamLensesPath(flowId: string): string {
    return this.sitePath(SiteUrls.flowLineageDownstreamLensesPart(flowId));
  }
  private static flowLineageDownstreamLensesPart(flowId: string): string {
    return `${SiteUrls.flowLineagePart(
      flowId
    )}${SiteUrls.downstreamPart()}${SiteUrls.lensesPart()}`;
  }

  public flowLineageDownstreamWorkbooksUrl(flowId: string): string {
    return this.fullUrl(this.flowLineageDownstreamWorkbooksPath(flowId));
  }
  public flowLineageDownstreamWorkbooksPath(flowId: string): string {
    return this.sitePath(SiteUrls.flowLineageDownstreamWorkbooksPart(flowId));
  }
  private static flowLineageDownstreamWorkbooksPart(flowId: string): string {
    return `${SiteUrls.flowLineagePart(
      flowId
    )}${SiteUrls.downstreamPart()}${SiteUrls.workbooksPart()}`;
  }

  public flowLineageDownstreamDashboardsUrl(flowId: string): string {
    return this.fullUrl(this.flowLineageDownstreamDashboardsPath(flowId));
  }
  public flowLineageDownstreamDashboardsPath(flowId: string): string {
    return this.sitePath(SiteUrls.flowLineageDownstreamDashboardsPart(flowId));
  }
  private static flowLineageDownstreamDashboardsPart(flowId: string): string {
    return `${SiteUrls.flowLineagePart(
      flowId
    )}${SiteUrls.downstreamPart()}${SiteUrls.dashboardsPart()}`;
  }

  public flowLineageDownstreamSheetsUrl(flowId: string): string {
    return this.fullUrl(this.flowLineageDownstreamSheetsPath(flowId));
  }
  public flowLineageDownstreamSheetsPath(flowId: string): string {
    return this.sitePath(SiteUrls.flowLineageDownstreamSheetsPart(flowId));
  }
  private static flowLineageDownstreamSheetsPart(flowId: string): string {
    return `${SiteUrls.flowLineagePart(
      flowId
    )}${SiteUrls.downstreamPart()}${SiteUrls.sheetsPart()}`;
  }

  public flowLineageDownstreaMetricsUrl(flowId: string): string {
    return this.fullUrl(this.flowLineageDownstreamMetricsPath(flowId));
  }
  public flowLineageDownstreamMetricsPath(flowId: string): string {
    return this.sitePath(SiteUrls.flowLineageDownstreamMetricsPart(flowId));
  }
  private static flowLineageDownstreamMetricsPart(flowId: string): string {
    return `${SiteUrls.flowLineagePart(
      flowId
    )}${SiteUrls.downstreamPart()}${SiteUrls.lineageMetricsPart()}`;
  }

  public flowLineageDownstreamOwnersUrl(flowId: string): string {
    return this.fullUrl(this.flowLineageDownstreamOwnersPath(flowId));
  }
  public flowLineageDownstreamOwnersPath(flowId: string): string {
    return this.sitePath(SiteUrls.flowLineageDownstreamOwnersPart(flowId));
  }
  private static flowLineageDownstreamOwnersPart(flowId: string): string {
    return `${SiteUrls.flowLineagePart(
      flowId
    )}${SiteUrls.downstreamPart()}${SiteUrls.ownersPart()}`;
  }

  public flowPublishresultUrl(flowId: string): string {
    return this.fullUrl(this.flowPublishresultPath(flowId));
  }
  public flowPublishresultPath(flowId: string): string {
    return this.sitePath(SiteUrls.flowPublishresultPart(flowId));
  }
  private static flowPublishresultPart(flowId: string): string {
    return `${SiteUrls.flowPart(flowId)}${SiteUrls.publishresultPart()}`;
  }

  // Projects

  public projectsUrl(): string {
    return this.fullUrl(this.projectsPath());
  }
  public projectsPath(): string {
    return this.sitePath(SiteUrls.projectsPart());
  }
  private static projectsPart(): string {
    return '/projects';
  }

  public projectUrl(projectId: string): string {
    return this.fullUrl(this.projectPath(projectId));
  }
  public projectPath(projectId: string): string {
    return this.sitePath(SiteUrls.projectPart(projectId));
  }
  private static projectPart(projectId: string): string {
    return `${SiteUrls.projectsPart()}/${projectId}`;
  }

  public projectViewsUrl(projectId: string): string {
    return this.fullUrl(this.projectViewsPath(projectId));
  }
  public projectViewsPath(projectId: string): string {
    return this.sitePath(SiteUrls.projectViewsPart(projectId));
  }
  private static projectViewsPart(projectId: string): string {
    return `${SiteUrls.projectPart(projectId)}${SiteUrls.viewsPart()}`;
  }

  // TODO: remove in favor of redirectToProjectUrl?
  public favoriteProjectUrl(projectId: string): string {
    return this.redirectToProjectUrl(projectId);
  }

  public redirectToProjectUrl(projectId: string): string {
    return this.fullUrl(this.redirectToProjectPath(projectId));
  }
  public redirectToProjectPath(projectId: string): string {
    return this.sitePath(SiteUrls.redirectToProjectPart(projectId));
  }
  private static redirectToProjectPart(projectId: string): string {
    return `/redirect_to_project/${projectId}`;
  }

  // FlowEditor
  public flowEditorPath(): string {
    return this.sitePath(SiteUrls.flowEditorPart());
  }

  public flowEditorDraftUrl(draftLuid: string): string {
    return this.flowEditorDraftPath(draftLuid);
  }

  public flowEditorDraftPath(draftLuid: string): string {
    return `${this.flowEditorPath()}${SiteUrls.flowEditorDraftPart(draftLuid)}`;
  }

  public flowEditorFlowPath(params: {
    flowLuid: string;
    datasourceUrlName?: string;
    graphQLDatabaseId?: string;
    graphQLTableId?: string
    projectId?: string;
  }): string {
    const { flowLuid, datasourceUrlName, graphQLDatabaseId, graphQLTableId, projectId } = params;
    return `${this.flowEditorPath()}${SiteUrls.flowEditorFlowsPart()}/${flowLuid}${SiteUrls.flowEditorFlowParamPart(
      {
        datasourceUrlName: datasourceUrlName,
        graphQLDatabaseId: graphQLDatabaseId,
        graphQLTableId: graphQLTableId,
        projectId: projectId,
      }
    )}`;
  }

  public flowEditorEmbeddingPath(): string {
    return `${this.flowEditorPath()}/embedding/tableau.prep.embedding.2.latest.js`;
  }

  private static flowEditorPart(): string {
    return '/flow-editor';
  }

  private static flowEditorDraftPart(draftLuid: string): string {
    return `/drafts/${draftLuid}`;
  }

  private static flowEditorFlowsPart(): string {
    return '/flows';
  }

  private static flowEditorFlowParamPart(params: {
    datasourceUrlName?: string;
    graphQLDatabaseId?: string;
    graphQLTableId?: string
    projectId?: string;
  }): string {
    const urlSearchParams = new URLSearchParams();
    if (params.datasourceUrlName) {
      urlSearchParams.set('datasource', params.datasourceUrlName);
    }

    if (params.graphQLDatabaseId) {
      urlSearchParams.set('graphQLDatabaseId', params.graphQLDatabaseId);
    }

    if (params.graphQLTableId) {
      urlSearchParams.set('graphQLTableId', params.graphQLTableId);
    }

    if (params.projectId) {
      urlSearchParams.set('projectId', params.projectId);
    }

    const paramPart: string = urlSearchParams.toString();

    return paramPart === '' ? paramPart : `?${paramPart}`;
  }

  // VirtualConnections
  public virtualConnectionsUrl(): string {
    return this.fullUrl(this.virtualConnectionsPath());
  }
  public virtualConnectionsPath(): string {
    return this.sitePath(SiteUrls.virtualConnectionsPart());
  }
  private static virtualConnectionsPart(): string {
    return '/virtualConnections';
  }

  public virtualConnectionsWithTagUrl(tag: string): string {
    return this.fullUrl(this.virtualConnectionsWithTagPath(tag));
  }
  public virtualConnectionsWithTagPath(tag: string): string {
    return this.sitePath(SiteUrls.virtualConnectionsWithTagPart(tag));
  }
  private static virtualConnectionsWithTagPart(tag: string): string {
    return `${SiteUrls.virtualConnectionsPart()}${SiteUrls.tagPart(tag)}`;
  }

  public favoriteVirtualConnectionUrl(id: string): string {
    return this.redirectToVirtualConnectionUrl(id);
  }

  public virtualConnectionUrl(id: string): string {
    return this.fullUrl(this.virtualConnectionPath(id));
  }
  public virtualConnectionPath(id: string): string {
    return this.sitePath(SiteUrls.virtualConnectionPart(id));
  }
  private static virtualConnectionPart(id: string): string {
    return `${SiteUrls.virtualConnectionsPart()}/${id}`;
  }

  public redirectToVirtualConnectionUrl(id: string): string {
    return this.fullUrl(this.redirectToVirtualConnectionPath(id));
  }
  public redirectToVirtualConnectionPath(id: string): string {
    return this.sitePath(SiteUrls.redirectToVirtualConnectionPart(id));
  }
  private static redirectToVirtualConnectionPart(id: string): string {
    return `/redirect_to_virtual_connection/${id}`;
  }

  public virtualConnectionOverviewUrl(virtualConnectionId: string): string {
    return this.fullUrl(
      this.virtualConnectionOverviewPath(virtualConnectionId)
    );
  }
  public virtualConnectionOverviewPath(
    virtualConnectionId: string
  ): string {
    return this.sitePath(
      SiteUrls.virtualConnectionOverviewPart(virtualConnectionId)
    );
  }
  private static virtualConnectionOverviewPart(
    virtualConnectionId: string
  ): string {
    return `${SiteUrls.virtualConnectionPart(
      virtualConnectionId
    )}${SiteUrls.overviewPart()}`;
  }

  public virtualConnectionScheduledTasksUrl(virtualConnectionId: string): string {
    return this.fullUrl(this.virtualConnectionScheduledTasksPath(virtualConnectionId));
  }
  public virtualConnectionScheduledTasksPath(virtualConnectionId: string): string {
    return this.sitePath(
      SiteUrls.virtualConnectionScheduledTasksPart(virtualConnectionId)
    );
  }
  private static virtualConnectionScheduledTasksPart(virtualConnectionId: string): string {
    return `${SiteUrls.virtualConnectionPart(
      virtualConnectionId
    )}${SiteUrls.scheduledTasksPart()}`;
  }

  public virtualConnectionLineageUrl(virtualConnectionId: string): string {
    return this.fullUrl(this.virtualConnectionLineagePath(virtualConnectionId));
  }
  public virtualConnectionLineagePath(virtualConnectionId: string): string {
    return this.sitePath(
      SiteUrls.virtualConnectionLineagePart(virtualConnectionId)
    );
  }
  private static virtualConnectionLineagePart(virtualConnectionId: string): string {
    return `${SiteUrls.virtualConnectionPart(
      virtualConnectionId
    )}${SiteUrls.lineagePart()}`;
  }

  public virtualConnectionLineageVirtualConnectionTablesUrl(virtualConnectionId: string): string {
    return this.fullUrl(this.virtualConnectionLineageVirtualConnectionTablesPath(virtualConnectionId));
  }
  public virtualConnectionLineageVirtualConnectionTablesPath(virtualConnectionId: string): string {
    return this.sitePath(SiteUrls.virtualConnectionLineageVirtualConnectionTablesPart(virtualConnectionId));
  }
  private static virtualConnectionLineageVirtualConnectionTablesPart(virtualConnectionId: string): string {
    return `${SiteUrls.virtualConnectionLineagePart(virtualConnectionId)}${SiteUrls.tablesPart()}`;
  }

  public virtualConnectionLineageVirtualConnectionTableUrl(
    virtualConnectionId: string, virtualConnectionTableId: string
  ): string {
    return this.fullUrl(this.virtualConnectionLineageVirtualConnectionTablePath(virtualConnectionId, virtualConnectionTableId));
  }
  public virtualConnectionLineageVirtualConnectionTablePath(
    virtualConnectionId: string, virtualConnectionTableId: string
  ): string {
    return this.sitePath(
      SiteUrls.virtualConnectionLineageVirtualConnectionTablePart(virtualConnectionId, virtualConnectionTableId)
    );
  }
  private static virtualConnectionLineageVirtualConnectionTablePart(
    virtualConnectionId: string, virtualConnectionTableId: string
  ): string {
    return `${SiteUrls.virtualConnectionLineagePart(
      virtualConnectionId
    )}${SiteUrls.tablesPart()}/${virtualConnectionTableId}`;
  }

  public virtualConnectionLineageUpstreamDatabasesUrl(virtualConnectionId: string): string {
    return this.fullUrl(this.virtualConnectionLineageUpstreamDatabasesPath(virtualConnectionId));
  }
  public virtualConnectionLineageUpstreamDatabasesPath(virtualConnectionId: string): string {
    return this.sitePath(SiteUrls.virtualConnectionLineageUpstreamDatabasesPart(virtualConnectionId));
  }
  private static virtualConnectionLineageUpstreamDatabasesPart(virtualConnectionId: string): string {
    return `${SiteUrls.virtualConnectionLineagePart(
      virtualConnectionId
    )}${SiteUrls.upstreamPart()}${SiteUrls.databasesPart()}`;
  }

  public virtualConnectionLineageUpstreamTablesUrl(virtualConnectionId: string): string {
    return this.fullUrl(this.virtualConnectionLineageUpstreamTablesPath(virtualConnectionId));
  }
  public virtualConnectionLineageUpstreamTablesPath(virtualConnectionId: string): string {
    return this.sitePath(SiteUrls.virtualConnectionLineageUpstreamTablesPart(virtualConnectionId));
  }
  private static virtualConnectionLineageUpstreamTablesPart(virtualConnectionId: string): string {
    return `${SiteUrls.virtualConnectionLineagePart(
      virtualConnectionId
    )}${SiteUrls.upstreamPart()}${SiteUrls.tablesPart()}`;
  }

  public virtualConnectionLineageDownstreamVirtualConnectionTablesUrl(virtualConnectionId: string): string {
    return this.fullUrl(this.virtualConnectionLineageDownstreamVirtualConnectionTablesPath(virtualConnectionId));
  }
  public virtualConnectionLineageDownstreamVirtualConnectionTablesPath(virtualConnectionId: string): string {
    return this.sitePath(SiteUrls.virtualConnectionLineageDownstreamVirtualConnectionTablesPart(virtualConnectionId));
  }
  private static virtualConnectionLineageDownstreamVirtualConnectionTablesPart(virtualConnectionId: string): string {
    return `${SiteUrls.virtualConnectionLineagePart(
      virtualConnectionId
    )}${SiteUrls.downstreamPart()}${SiteUrls.virtualConnectionTablesPart()}`;
  }

  public virtualConnectionLineageDownstreamFlowsUrl(virtualConnectionId: string): string {
    return this.fullUrl(this.virtualConnectionLineageDownstreamFlowsPath(virtualConnectionId));
  }
  public virtualConnectionLineageDownstreamFlowsPath(virtualConnectionId: string): string {
    return this.sitePath(SiteUrls.virtualConnectionLineageDownstreamFlowsPart(virtualConnectionId));
  }
  private static virtualConnectionLineageDownstreamFlowsPart(virtualConnectionId: string): string {
    return `${SiteUrls.virtualConnectionLineagePart(
      virtualConnectionId
    )}${SiteUrls.downstreamPart()}${SiteUrls.flowsPart()}`;
  }

  public virtualConnectionLineageDownstreamDatasourcesUrl(virtualConnectionId: string): string {
    return this.fullUrl(this.virtualConnectionLineageDownstreamDatasourcesPath(virtualConnectionId));
  }
  public virtualConnectionLineageDownstreamDatasourcesPath(virtualConnectionId: string): string {
    return this.sitePath(SiteUrls.virtualConnectionLineageDownstreamDatasourcesPart(virtualConnectionId));
  }
  private static virtualConnectionLineageDownstreamDatasourcesPart(virtualConnectionId: string): string {
    return `${SiteUrls.virtualConnectionLineagePart(
      virtualConnectionId
    )}${SiteUrls.downstreamPart()}${SiteUrls.datasourcesPart()}`;
  }

  public virtualConnectionLineageDownstreamLensesUrl(virtualConnectionId: string): string {
    return this.fullUrl(this.virtualConnectionLineageDownstreamLensesPath(virtualConnectionId));
  }
  public virtualConnectionLineageDownstreamLensesPath(virtualConnectionId: string): string {
    return this.sitePath(SiteUrls.virtualConnectionLineageDownstreamLensesPart(virtualConnectionId));
  }
  private static virtualConnectionLineageDownstreamLensesPart(virtualConnectionId: string): string {
    return `${SiteUrls.virtualConnectionLineagePart(
      virtualConnectionId
    )}${SiteUrls.downstreamPart()}${SiteUrls.lensesPart()}`;
  }

  public virtualConnectionLineageDownstreamWorkbooksUrl(virtualConnectionId: string): string {
    return this.fullUrl(this.virtualConnectionLineageDownstreamWorkbooksPath(virtualConnectionId));
  }
  public virtualConnectionLineageDownstreamWorkbooksPath(virtualConnectionId: string): string {
    return this.sitePath(SiteUrls.virtualConnectionLineageDownstreamWorkbooksPart(virtualConnectionId));
  }
  private static virtualConnectionLineageDownstreamWorkbooksPart(virtualConnectionId: string): string {
    return `${SiteUrls.virtualConnectionLineagePart(
      virtualConnectionId
    )}${SiteUrls.downstreamPart()}${SiteUrls.workbooksPart()}`;
  }

  public virtualConnectionLineageDownstreamDashboardsUrl(virtualConnectionId: string): string {
    return this.fullUrl(this.virtualConnectionLineageDownstreamDashboardsPath(virtualConnectionId));
  }
  public virtualConnectionLineageDownstreamDashboardsPath(virtualConnectionId: string): string {
    return this.sitePath(SiteUrls.virtualConnectionLineageDownstreamDashboardsPart(virtualConnectionId));
  }
  private static virtualConnectionLineageDownstreamDashboardsPart(virtualConnectionId: string): string {
    return `${SiteUrls.virtualConnectionLineagePart(
      virtualConnectionId
    )}${SiteUrls.downstreamPart()}${SiteUrls.dashboardsPart()}`;
  }

  public virtualConnectionLineageDownstreamSheetsUrl(virtualConnectionId: string): string {
    return this.fullUrl(this.virtualConnectionLineageDownstreamSheetsPath(virtualConnectionId));
  }
  public virtualConnectionLineageDownstreamSheetsPath(virtualConnectionId: string): string {
    return this.sitePath(SiteUrls.virtualConnectionLineageDownstreamSheetsPart(virtualConnectionId));
  }
  private static virtualConnectionLineageDownstreamSheetsPart(virtualConnectionId: string): string {
    return `${SiteUrls.virtualConnectionLineagePart(
      virtualConnectionId
    )}${SiteUrls.downstreamPart()}${SiteUrls.sheetsPart()}`;
  }

  public virtualConnectionLineageDownstreamMetricsUrl(virtualConnectionId: string): string {
    return this.fullUrl(this.virtualConnectionLineageDownstreamMetricsPath(virtualConnectionId));
  }
  public virtualConnectionLineageDownstreamMetricsPath(virtualConnectionId: string): string {
    return this.sitePath(SiteUrls.virtualConnectionLineageDownstreamMetricsPart(virtualConnectionId));
  }
  private static virtualConnectionLineageDownstreamMetricsPart(virtualConnectionId: string): string {
    return `${SiteUrls.virtualConnectionLineagePart(
      virtualConnectionId
    )}${SiteUrls.downstreamPart()}${SiteUrls.lineageMetricsPart()}`;
  }

  public virtualConnectionLineageDownstreamOwnersUrl(virtualConnectionId: string): string {
    return this.fullUrl(this.virtualConnectionLineageDownstreamOwnersPath(virtualConnectionId));
  }
  public virtualConnectionLineageDownstreamOwnersPath(virtualConnectionId: string): string {
    return this.sitePath(SiteUrls.virtualConnectionLineageDownstreamOwnersPart(virtualConnectionId));
  }
  private static virtualConnectionLineageDownstreamOwnersPart(virtualConnectionId: string): string {
    return `${SiteUrls.virtualConnectionLineagePart(
      virtualConnectionId
    )}${SiteUrls.downstreamPart()}${SiteUrls.ownersPart()}`;
  }

  public virtualConnectionRunHistoryUrl(virtualConnectionId: string): string {
    return this.fullUrl(this.virtualConnectionRunHistoryPath(virtualConnectionId));
  }
  public virtualConnectionRunHistoryPath(virtualConnectionId: string): string {
    return this.sitePath(SiteUrls.virtualConnectionRunHistoryPart(virtualConnectionId));
  }
  private static virtualConnectionRunHistoryPart(virtualConnectionId: string): string {
    return `${SiteUrls.virtualConnectionPart(virtualConnectionId)}${SiteUrls.runHistoryPart()}`;
  }

  // VirtualConnectionTables
  public virtualConnectionTablesUrl(): string {
    return this.fullUrl(this.virtualConnectionTablesPath());
  }
  public virtualConnectionTablesPath(): string {
    return this.sitePath(SiteUrls.virtualConnectionTablesPart());
  }
  private static virtualConnectionTablesPart(): string {
    return '/virtualConnectionTables';
  }

  public virtualConnectionTablesWithTagUrl(tag: string): string {
    return this.fullUrl(this.virtualConnectionTablesWithTagPath(tag));
  }
  public virtualConnectionTablesWithTagPath(tag: string): string {
    return this.sitePath(SiteUrls.virtualConnectionTablesWithTagPart(tag));
  }
  private static virtualConnectionTablesWithTagPart(tag: string): string {
    return `${SiteUrls.virtualConnectionTablesPart()}${SiteUrls.tagPart(
      tag
    )}`;
  }

  public favoriteVirtualConnectionTableUrl(id: string): string {
    return this.redirectToVirtualConnectionTableUrl(id);
  }

  public virtualConnectionTableUrl(id: string): string {
    return this.fullUrl(this.virtualConnectionTablePath(id));
  }
  public virtualConnectionTablePath(id: string): string {
    return this.sitePath(SiteUrls.virtualConnectionTablePart(id));
  }
  private static virtualConnectionTablePart(id: string): string {
    return `${SiteUrls.virtualConnectionTablesPart()}/${id}`;
  }

  public redirectToVirtualConnectionTableUrl(id: string): string {
    return this.fullUrl(this.redirectToVirtualConnectionTablePath(id));
  }
  public redirectToVirtualConnectionTablePath(id: string): string {
    return this.sitePath(SiteUrls.redirectToVirtualConnectionTablePart(id));
  }
  private static redirectToVirtualConnectionTablePart(id: string): string {
    return `/redirect_to_virtual_connection_table/${id}`;
  }

  public virtualConnectionTableOverviewUrl(id: string): string {
    return this.fullUrl(this.virtualConnectionTableOverviewPath(id));
  }
  public virtualConnectionTableOverviewPath(id: string): string {
    return this.sitePath(SiteUrls.virtualConnectionTableOverviewPart(id));
  }
  private static virtualConnectionTableOverviewPart(id: string): string {
    return `${SiteUrls.virtualConnectionTablePart(
      id
    )}${SiteUrls.overviewPart()}`;
  }

  public virtualConnectionTableScheduledTasksUrl(id: string): string {
    return this.fullUrl(this.virtualConnectionTableScheduledTasksPath(id));
  }
  public virtualConnectionTableScheduledTasksPath(id: string): string {
    return this.sitePath(
      SiteUrls.virtualConnectionTableScheduledTasksPart(id)
    );
  }
  private static virtualConnectionTableScheduledTasksPart(
    id: string
  ): string {
    return `${SiteUrls.virtualConnectionTablePart(
      id
    )}${SiteUrls.scheduledTasksPart()}`;
  }

  public virtualConnectionTableLineageUrl(virtualConnectionTableId: string): string {
    return this.fullUrl(this.virtualConnectionTableLineagePath(virtualConnectionTableId));
  }
  public virtualConnectionTableLineagePath(virtualConnectionTableId: string): string {
    return this.sitePath(
      SiteUrls.virtualConnectionTableLineagePart(virtualConnectionTableId)
    );
  }
  private static virtualConnectionTableLineagePart(virtualConnectionTableId: string): string {
    return `${SiteUrls.virtualConnectionTablePart(
      virtualConnectionTableId
    )}${SiteUrls.lineagePart()}`;
  }

  public virtualConnectionTableLineageFieldsUrl(virtualConnectionTableId: string): string {
    return this.fullUrl(this.virtualConnectionTableLineageFieldsPath(virtualConnectionTableId));
  }
  public virtualConnectionTableLineageFieldsPath(virtualConnectionTableId: string): string {
    return this.sitePath(SiteUrls.virtualConnectionTableLineageFieldsPart(virtualConnectionTableId));
  }
  private static virtualConnectionTableLineageFieldsPart(virtualConnectionTableId: string): string {
    return `${SiteUrls.virtualConnectionTableLineagePart(virtualConnectionTableId)}${SiteUrls.fieldsPart()}`;
  }

  public virtualConnectionTableLineageFieldUrl(virtualConnectionTableId: string, fieldId: string): string {
    return this.fullUrl(this.virtualConnectionTableLineageFieldPath(virtualConnectionTableId, fieldId));
  }
  public virtualConnectionTableLineageFieldPath(virtualConnectionTableId: string, fieldId: string): string {
    return this.sitePath(SiteUrls.virtualConnectionTableLineageFieldPart(virtualConnectionTableId, fieldId));
  }
  private static virtualConnectionTableLineageFieldPart(virtualConnectionTableId: string, fieldId: string): string {
    return `${SiteUrls.virtualConnectionTableLineagePart(
      virtualConnectionTableId
    )}${SiteUrls.fieldsPart()}/${fieldId}`;
  }

  public virtualConnectionTableLineageUpstreamDatabasesUrl(virtualConnectionTableId: string): string {
    return this.fullUrl(this.virtualConnectionTableLineageUpstreamDatabasesPath(virtualConnectionTableId));
  }
  public virtualConnectionTableLineageUpstreamDatabasesPath(virtualConnectionTableId: string): string {
    return this.sitePath(SiteUrls.virtualConnectionTableLineageUpstreamDatabasesPart(virtualConnectionTableId));
  }
  private static virtualConnectionTableLineageUpstreamDatabasesPart(virtualConnectionTableId: string): string {
    return `${SiteUrls.virtualConnectionTableLineagePart(
      virtualConnectionTableId
    )}${SiteUrls.upstreamPart()}${SiteUrls.databasesPart()}`;
  }

  public virtualConnectionTableLineageUpstreamTablesUrl(virtualConnectionTableId: string): string {
    return this.fullUrl(this.virtualConnectionTableLineageUpstreamTablesPath(virtualConnectionTableId));
  }
  public virtualConnectionTableLineageUpstreamTablesPath(virtualConnectionTableId: string): string {
    return this.sitePath(SiteUrls.virtualConnectionTableLineageUpstreamTablesPart(virtualConnectionTableId));
  }
  private static virtualConnectionTableLineageUpstreamTablesPart(virtualConnectionTableId: string): string {
    return `${SiteUrls.virtualConnectionTableLineagePart(
      virtualConnectionTableId
    )}${SiteUrls.upstreamPart()}${SiteUrls.tablesPart()}`;
  }

  public virtualConnectionTableLineageUpstreamVirtualConnectionsUrl(virtualConnectionTableId: string): string {
    return this.fullUrl(this.virtualConnectionTableLineageUpstreamVirtualConnectionsPath(virtualConnectionTableId));
  }
  public virtualConnectionTableLineageUpstreamVirtualConnectionsPath(virtualConnectionTableId: string): string {
    return this.sitePath(SiteUrls.virtualConnectionTableLineageUpstreamVirtualConnectionsPart(virtualConnectionTableId));
  }
  private static virtualConnectionTableLineageUpstreamVirtualConnectionsPart(virtualConnectionTableId: string): string {
    return `${SiteUrls.virtualConnectionTableLineagePart(
      virtualConnectionTableId
    )}${SiteUrls.upstreamPart()}${SiteUrls.virtualConnectionsPart()}`;
  }

  public virtualConnectionTableLineageDownstreamFlowsUrl(virtualConnectionTableId: string): string {
    return this.fullUrl(this.virtualConnectionTableLineageDownstreamFlowsPath(virtualConnectionTableId));
  }
  public virtualConnectionTableLineageDownstreamFlowsPath(virtualConnectionTableId: string): string {
    return this.sitePath(SiteUrls.virtualConnectionTableLineageDownstreamFlowsPart(virtualConnectionTableId));
  }
  private static virtualConnectionTableLineageDownstreamFlowsPart(virtualConnectionTableId: string): string {
    return `${SiteUrls.virtualConnectionTableLineagePart(
      virtualConnectionTableId
    )}${SiteUrls.downstreamPart()}${SiteUrls.flowsPart()}`;
  }

  public virtualConnectionTableLineageDownstreamDatasourcesUrl(virtualConnectionTableId: string): string {
    return this.fullUrl(this.virtualConnectionTableLineageDownstreamDatasourcesPath(virtualConnectionTableId));
  }
  public virtualConnectionTableLineageDownstreamDatasourcesPath(virtualConnectionTableId: string): string {
    return this.sitePath(SiteUrls.virtualConnectionTableLineageDownstreamDatasourcesPart(virtualConnectionTableId));
  }
  private static virtualConnectionTableLineageDownstreamDatasourcesPart(virtualConnectionTableId: string): string {
    return `${SiteUrls.virtualConnectionTableLineagePart(
      virtualConnectionTableId
    )}${SiteUrls.downstreamPart()}${SiteUrls.datasourcesPart()}`;
  }

  public virtualConnectionTableLineageDownstreamLensesUrl(virtualConnectionTableId: string): string {
    return this.fullUrl(this.virtualConnectionTableLineageDownstreamLensesPath(virtualConnectionTableId));
  }
  public virtualConnectionTableLineageDownstreamLensesPath(virtualConnectionTableId: string): string {
    return this.sitePath(SiteUrls.virtualConnectionTableLineageDownstreamLensesPart(virtualConnectionTableId));
  }
  private static virtualConnectionTableLineageDownstreamLensesPart(virtualConnectionTableId: string): string {
    return `${SiteUrls.virtualConnectionTableLineagePart(
      virtualConnectionTableId
    )}${SiteUrls.downstreamPart()}${SiteUrls.lensesPart()}`;
  }


  public virtualConnectionTableLineageDownstreamWorkbooksUrl(virtualConnectionTableId: string): string {
    return this.fullUrl(this.virtualConnectionTableLineageDownstreamWorkbooksPath(virtualConnectionTableId));
  }
  public virtualConnectionTableLineageDownstreamWorkbooksPath(virtualConnectionTableId: string): string {
    return this.sitePath(SiteUrls.virtualConnectionTableLineageDownstreamWorkbooksPart(virtualConnectionTableId));
  }
  private static virtualConnectionTableLineageDownstreamWorkbooksPart(virtualConnectionTableId: string): string {
    return `${SiteUrls.virtualConnectionTableLineagePart(
      virtualConnectionTableId
    )}${SiteUrls.downstreamPart()}${SiteUrls.workbooksPart()}`;
  }

  public virtualConnectionTableLineageDownstreamDashboardsUrl(virtualConnectionTableId: string): string {
    return this.fullUrl(this.virtualConnectionTableLineageDownstreamDashboardsPath(virtualConnectionTableId));
  }
  public virtualConnectionTableLineageDownstreamDashboardsPath(virtualConnectionTableId: string): string {
    return this.sitePath(SiteUrls.virtualConnectionTableLineageDownstreamDashboardsPart(virtualConnectionTableId));
  }
  private static virtualConnectionTableLineageDownstreamDashboardsPart(virtualConnectionTableId: string): string {
    return `${SiteUrls.virtualConnectionTableLineagePart(
      virtualConnectionTableId
    )}${SiteUrls.downstreamPart()}${SiteUrls.dashboardsPart()}`;
  }

  public virtualConnectionTableLineageDownstreamSheetsUrl(virtualConnectionTableId: string): string {
    return this.fullUrl(this.virtualConnectionTableLineageDownstreamSheetsPath(virtualConnectionTableId));
  }
  public virtualConnectionTableLineageDownstreamSheetsPath(virtualConnectionTableId: string): string {
    return this.sitePath(SiteUrls.virtualConnectionTableLineageDownstreamSheetsPart(virtualConnectionTableId));
  }
  private static virtualConnectionTableLineageDownstreamSheetsPart(virtualConnectionTableId: string): string {
    return `${SiteUrls.virtualConnectionTableLineagePart(
      virtualConnectionTableId
    )}${SiteUrls.downstreamPart()}${SiteUrls.sheetsPart()}`;
  }

  public virtualConnectionTableLineageDownstreamMetricsUrl(virtualConnectionTableId: string): string {
    return this.fullUrl(this.virtualConnectionTableLineageDownstreamMetricsPath(virtualConnectionTableId));
  }
  public virtualConnectionTableLineageDownstreamMetricsPath(virtualConnectionTableId: string): string {
    return this.sitePath(SiteUrls.virtualConnectionTableLineageDownstreamMetricsPart(virtualConnectionTableId));
  }
  private static virtualConnectionTableLineageDownstreamMetricsPart(virtualConnectionTableId: string): string {
    return `${SiteUrls.virtualConnectionTableLineagePart(
      virtualConnectionTableId
    )}${SiteUrls.downstreamPart()}${SiteUrls.lineageMetricsPart()}`;
  }

  public virtualConnectionTableLineageDownstreamOwnersUrl(virtualConnectionTableId: string): string {
    return this.fullUrl(this.virtualConnectionTableLineageDownstreamOwnersPath(virtualConnectionTableId));
  }
  public virtualConnectionTableLineageDownstreamOwnersPath(virtualConnectionTableId: string): string {
    return this.sitePath(SiteUrls.virtualConnectionTableLineageDownstreamOwnersPart(virtualConnectionTableId));
  }
  private static virtualConnectionTableLineageDownstreamOwnersPart(virtualConnectionTableId: string): string {
    return `${SiteUrls.virtualConnectionTableLineagePart(
      virtualConnectionTableId
    )}${SiteUrls.downstreamPart()}${SiteUrls.ownersPart()}`;
  }

  // Workbooks

  public workbooksUrl(): string {
    return this.fullUrl(this.workbooksPath());
  }
  public workbooksPath(): string {
    return this.sitePath(SiteUrls.workbooksPart());
  }
  private static workbooksPart(): string {
    return '/workbooks';
  }

  public workbooksWithTagUrl(tag: string): string {
    return this.fullUrl(this.workbooksWithTagPath(tag));
  }

  public workbooksWithTagPath(tag: string): string {
    return this.sitePath(SiteUrls.workbooksWithTagPart(tag));
  }

  private static workbooksWithTagPart(tag: string): string {
    return `${SiteUrls.workbooksPart()}${SiteUrls.tagPart(tag)}`;
  }

  public workbookUrl(workbookId: string): string {
    return this.fullUrl(this.workbookPath(workbookId));
  }
  public workbookPath(workbookId: string): string {
    return this.sitePath(SiteUrls.workbookPart(workbookId));
  }
  private static workbookPart(workbookId: string): string {
    return `${SiteUrls.workbooksPart()}/${workbookId}`;
  }

  public workbookViewsUrl(workbookId: string): string {
    return this.fullUrl(this.workbookViewsPath(workbookId));
  }
  public workbookViewsPath(workbookId: string): string {
    return this.sitePath(SiteUrls.workbookViewsPart(workbookId));
  }
  private static workbookViewsPart(workbookId: string): string {
    return `${SiteUrls.workbookPart(workbookId)}${SiteUrls.viewsPart()}`;
  }

  public workbookCustomViewsUrl(workbookId: string): string {
    return this.fullUrl(this.workbookCustomViewsPath(workbookId));
  }
  public workbookCustomViewsPath(workbookId: string): string {
    return this.sitePath(SiteUrls.workbookCustomViewsPart(workbookId));
  }
  private static workbookCustomViewsPart(workbookId: string): string {
    return `${SiteUrls.workbookPart(workbookId)}${SiteUrls.customViewsPart()}`;
  }

  public workbookSubscriptionsUrl(workbookId: string): string {
    return this.fullUrl(this.workbookSubscriptionsPath(workbookId));
  }
  public workbookSubscriptionsPath(workbookId: string): string {
    return this.sitePath(SiteUrls.workbookSubscriptionsPart(workbookId));
  }
  private static workbookSubscriptionsPart(workbookId: string): string {
    return `${SiteUrls.workbookPart(
      workbookId
    )}${SiteUrls.subscriptionsPart()}`;
  }

  public workbookExtractRefreshesUrl(workbookId: string): string {
    return this.fullUrl(this.workbookExtractRefreshesPath(workbookId));
  }
  public workbookExtractRefreshesPath(workbookId: string): string {
    return this.sitePath(SiteUrls.workbookExtractRefreshesPart(workbookId));
  }
  private static workbookExtractRefreshesPart(workbookId: string): string {
    return `${SiteUrls.workbookPart(
      workbookId
    )}${SiteUrls.extractRefreshesPart()}`;
  }

  public workbookDetailsUrl(workbookId: string): string {
    return this.fullUrl(this.workbookDetailsPath(workbookId));
  }
  public workbookDetailsPath(workbookId: string): string {
    return this.sitePath(SiteUrls.workbookDetailsPart(workbookId));
  }
  private static workbookDetailsPart(workbookId: string): string {
    return `${SiteUrls.workbookPart(workbookId)}${SiteUrls.detailsPart()}`;
  }

  public workbookPublishresultUrl(workbookId: string): string {
    return this.fullUrl(this.workbookPublishresultPath(workbookId));
  }
  public workbookPublishresultPath(workbookId: string): string {
    return this.sitePath(SiteUrls.workbookPublishresultPart(workbookId));
  }
  private static workbookPublishresultPart(workbookId: string): string {
    return `${SiteUrls.workbookPart(
      workbookId
    )}${SiteUrls.publishresultPart()}`;
  }
  private static publishresultPart(): string {
    return '/publishresult';
  }

  public workbookDatasourcesUrl(workbookId: string): string {
    return this.fullUrl(this.workbookDatasourcesPath(workbookId));
  }
  public workbookDatasourcesPath(workbookId: string): string {
    return this.sitePath(SiteUrls.workbookDatasourcesPart(workbookId));
  }
  private static workbookDatasourcesPart(workbookId: string): string {
    return `${SiteUrls.workbookPart(workbookId)}${SiteUrls.datasourcesPart()}`;
  }

  public workbookConnectedMetricsUrl(workbookId: string): string {
    return this.fullUrl(this.workbookConnectedMetricsPath(workbookId));
  }
  public workbookConnectedMetricsPath(workbookId: string): string {
    return this.sitePath(SiteUrls.workbookConnectedMetricsPart(workbookId));
  }
  private static workbookConnectedMetricsPart(workbookId: string): string {
    return `${SiteUrls.workbookPart(
      workbookId
    )}${SiteUrls.connectedMetricsPart()}`;
  }
  private static connectedMetricsPart(): string {
    return '/connectedMetrics';
  }

  // TODO: remove in favor of redirectToWorkbookUrl?
  public favoriteWorkbookUrl(workbookId: string): string {
    return this.redirectToWorkbookUrl(workbookId);
  }

  public redirectToWorkbookUrl(workbookId: string): string {
    return this.fullUrl(this.redirectToWorkbookPath(workbookId));
  }
  public redirectToWorkbookPath(workbookId: string): string {
    return this.sitePath(SiteUrls.redirectToWorkbookPart(workbookId));
  }
  private static redirectToWorkbookPart(workbookId: string): string {
    return `/redirect_to_workbook/${workbookId}`;
  }

  public workbookRevisionPreviewUrl(revisionPath: string): string {
    return this.fullUrl(this.workbookRevisionPreviewPath(revisionPath));
  }
  public workbookRevisionPreviewPath(revisionPath: string): string {
    return this.sitePath(SiteUrls.workbookRevisionPreviewPart(revisionPath));
  }
  private static workbookRevisionPreviewPart(revisionPath: string): string {
    return `/${revisionPath}`;
  }

  public workbookLineageUrl(workbookId: string): string {
    return this.fullUrl(this.workbookLineagePath(workbookId));
  }
  public workbookLineagePath(workbookId: string): string {
    return this.sitePath(SiteUrls.workbookLineagePart(workbookId));
  }
  private static workbookLineagePart(workbookId: string): string {
    return `${SiteUrls.workbookPart(workbookId)}${SiteUrls.lineagePart()}`;
  }

  public workbookLineageFieldsUrl(
    workbookId: string,
    embeddedDatasourceId?: string
  ): string {
    return this.fullUrl(
      this.workbookLineageFieldsPath(workbookId, embeddedDatasourceId)
    );
  }
  public workbookLineageFieldsPath(
    workbookId: string,
    embeddedDatasourceId?: string
  ): string {
    return this.sitePath(
      SiteUrls.workbookLineageFieldsPart(workbookId, embeddedDatasourceId)
    );
  }
  private static workbookLineageFieldsPart(
    workbookId: string,
    embeddedDatasourceId?: string
  ): string {
    const workbookLineagePart = SiteUrls.workbookLineagePart(workbookId);
    const fieldsPart = SiteUrls.fieldsPart();
    const embeddedDatasourceParamPart =
      this.workbookLineageEmbeddedDatasourceParamPart(embeddedDatasourceId);
    return `${workbookLineagePart}${fieldsPart}${embeddedDatasourceParamPart}`;
  }
  private static workbookLineageEmbeddedDatasourceParamPart(
    embeddedDatasourceId?: string
  ): string {
    return embeddedDatasourceId === undefined
      ? ''
      : `?${CatalogUrlParamKeys.embeddedDataSource}=${embeddedDatasourceId}`;
  }

  public workbookLineageFieldUrl(workbookId: string, fieldId: string): string {
    return this.fullUrl(this.workbookLineageFieldPath(workbookId, fieldId));
  }
  public workbookLineageFieldPath(workbookId: string, fieldId: string): string {
    return this.sitePath(
      SiteUrls.workbookLineageFieldPart(workbookId, fieldId)
    );
  }
  private static workbookLineageFieldPart(
    workbookId: string,
    fieldId: string
  ): string {
    return `${SiteUrls.workbookLineagePart(
      workbookId
    )}${SiteUrls.fieldsPart()}/${fieldId}`;
  }

  public workbookLineageUpstreamDatabasesUrl(workbookId: string): string {
    return this.fullUrl(this.workbookLineageUpstreamDatabasesPath(workbookId));
  }
  public workbookLineageUpstreamDatabasesPath(workbookId: string): string {
    return this.sitePath(
      SiteUrls.workbookLineageUpstreamDatabasesPart(workbookId)
    );
  }
  private static workbookLineageUpstreamDatabasesPart(
    workbookId: string
  ): string {
    return `${SiteUrls.workbookLineagePart(
      workbookId
    )}${SiteUrls.upstreamPart()}${SiteUrls.databasesPart()}`;
  }

  public workbookLineageUpstreamTablesUrl(workbookId: string): string {
    return this.fullUrl(this.workbookLineageUpstreamTablesPath(workbookId));
  }
  public workbookLineageUpstreamTablesPath(workbookId: string): string {
    return this.sitePath(
      SiteUrls.workbookLineageUpstreamTablesPart(workbookId)
    );
  }
  private static workbookLineageUpstreamTablesPart(workbookId: string): string {
    return `${SiteUrls.workbookLineagePart(
      workbookId
    )}${SiteUrls.upstreamPart()}${SiteUrls.tablesPart()}`;
  }

  public workbookLineageUpstreamVirtualConnectionsUrl(workbookId: string): string {
    return this.fullUrl(this.workbookLineageUpstreamVirtualConnectionsPath(workbookId));
  }
  public workbookLineageUpstreamVirtualConnectionsPath(workbookId: string): string {
    return this.sitePath(SiteUrls.workbookLineageUpstreamVirtualConnectionsPart(workbookId));
  }
  private static workbookLineageUpstreamVirtualConnectionsPart(workbookId: string): string {
    return `${SiteUrls.workbookLineagePart(
      workbookId
    )}${SiteUrls.upstreamPart()}${SiteUrls.virtualConnectionsPart()}`;
  }

  public workbookLineageUpstreamVirtualConnectionTablesUrl(workbookId: string): string {
    return this.fullUrl(this.workbookLineageUpstreamVirtualConnectionTablesPath(workbookId));
  }
  public workbookLineageUpstreamVirtualConnectionTablesPath(workbookId: string): string {
    return this.sitePath(SiteUrls.workbookLineageUpstreamVirtualConnectionTablesPart(workbookId));
  }
  private static workbookLineageUpstreamVirtualConnectionTablesPart(workbookId: string): string {
    return `${SiteUrls.workbookLineagePart(
      workbookId
    )}${SiteUrls.upstreamPart()}${SiteUrls.virtualConnectionTablesPart()}`;
  }

  public workbookLineageUpstreamFlowsUrl(workbookId: string): string {
    return this.fullUrl(this.workbookLineageUpstreamFlowsPath(workbookId));
  }
  public workbookLineageUpstreamFlowsPath(workbookId: string): string {
    return this.sitePath(SiteUrls.workbookLineageUpstreamFlowsPart(workbookId));
  }
  private static workbookLineageUpstreamFlowsPart(workbookId: string): string {
    return `${SiteUrls.workbookLineagePart(
      workbookId
    )}${SiteUrls.upstreamPart()}${SiteUrls.flowsPart()}`;
  }

  public workbookLineageUpstreamDatasourcesUrl(workbookId: string): string {
    return this.fullUrl(
      this.workbookLineageUpstreamDatasourcesPath(workbookId)
    );
  }
  public workbookLineageUpstreamDatasourcesPath(workbookId: string): string {
    return this.sitePath(
      SiteUrls.workbookLineageUpstreamDatasourcesPart(workbookId)
    );
  }
  private static workbookLineageUpstreamDatasourcesPart(
    workbookId: string
  ): string {
    return `${SiteUrls.workbookLineagePart(
      workbookId
    )}${SiteUrls.upstreamPart()}${SiteUrls.datasourcesPart()}`;
  }

  public workbookLineageUpstreamLensesUrl(workbookId: string): string {
    return this.fullUrl(
      this.workbookLineageUpstreamLensesPath(workbookId)
    );
  }
  public workbookLineageUpstreamLensesPath(workbookId: string): string {
    return this.sitePath(
      SiteUrls.workbookLineageUpstreamLensesPart(workbookId)
    );
  }
  private static workbookLineageUpstreamLensesPart(
    workbookId: string
  ): string {
    return `${SiteUrls.workbookLineagePart(
      workbookId
    )}${SiteUrls.upstreamPart()}${SiteUrls.lensesPart()}`;
  }

  public workbookLineageDownstreamDashboardsUrl(workbookId: string): string {
    return this.fullUrl(
      this.workbookLineageDownstreamDashboardsPath(workbookId)
    );
  }
  public workbookLineageDownstreamDashboardsPath(workbookId: string): string {
    return this.sitePath(
      SiteUrls.workbookLineageDownstreamDashboardsPart(workbookId)
    );
  }
  private static workbookLineageDownstreamDashboardsPart(
    workbookId: string
  ): string {
    return `${SiteUrls.workbookLineagePart(
      workbookId
    )}${SiteUrls.downstreamPart()}${SiteUrls.dashboardsPart()}`;
  }

  public workbookLineageDownstreamSheetsUrl(workbookId: string): string {
    return this.fullUrl(this.workbookLineageDownstreamSheetsPath(workbookId));
  }
  public workbookLineageDownstreamSheetsPath(workbookId: string): string {
    return this.sitePath(
      SiteUrls.workbookLineageDownstreamSheetsPart(workbookId)
    );
  }
  private static workbookLineageDownstreamSheetsPart(
    workbookId: string
  ): string {
    return `${SiteUrls.workbookLineagePart(
      workbookId
    )}${SiteUrls.downstreamPart()}${SiteUrls.sheetsPart()}`;
  }

  public workbookLineageDownstreamMetricsUrl(workbookId: string): string {
    return this.fullUrl(this.workbookLineageDownstreamMetricsPath(workbookId));
  }
  public workbookLineageDownstreamMetricsPath(workbookId: string): string {
    return this.sitePath(
      SiteUrls.workbookLineageDownstreamMetricsPart(workbookId)
    );
  }
  private static workbookLineageDownstreamMetricsPart(
    workbookId: string
  ): string {
    return `${SiteUrls.workbookLineagePart(
      workbookId
    )}${SiteUrls.downstreamPart()}${SiteUrls.lineageMetricsPart()}`;
  }

  public workbookLineageDownstreamOwnersUrl(tableId: string): string {
    return this.fullUrl(this.workbookLineageDownstreamOwnersPath(tableId));
  }
  public workbookLineageDownstreamOwnersPath(tableId: string): string {
    return this.sitePath(SiteUrls.workbookLineageDownstreamOwnersPart(tableId));
  }
  private static workbookLineageDownstreamOwnersPart(tableId: string): string {
    return `${SiteUrls.workbookLineagePart(
      tableId
    )}${SiteUrls.downstreamPart()}${SiteUrls.ownersPart()}`;
  }

  public workbookUsageUrl(workbookId: string): string {
    return this.fullUrl(this.workbookUsagePath(workbookId));
  }
  public workbookUsagePath(workbookId: string): string {
    return this.sitePath(SiteUrls.workbookUsagePart(workbookId));
  }
  private static workbookUsagePart(workbookId: string): string {
    return `${SiteUrls.workbookPart(workbookId)}${SiteUrls.usagePart()}`;
  }
  private static usagePart(): string {
    return '/usage';
  }

  public workbookUsageMetricUrl(params: {
    workbookId: string;
    usageMetricId: string;
  }): string {
    return this.fullUrl(this.workbookUsageMetricPath(params));
  }
  public workbookUsageMetricPath(params: {
    workbookId: string;
    usageMetricId: string;
  }): string {
    return this.sitePath(SiteUrls.workbookUsageMetricPart(params));
  }
  private static workbookUsageMetricPart(params: {
    workbookId: string;
    usageMetricId: string;
  }): string {
    return `${SiteUrls.workbookUsagePart(params.workbookId)}/${params.usageMetricId}`;
  }

  // Views

  public viewsUrl(): string {
    return this.fullUrl(this.viewsPath());
  }
  public viewsPath(): string {
    return this.sitePath(SiteUrls.viewsPart());
  }
  private static viewsPart(): string {
    return '/views';
  }

  public viewUrl(viewPath: string): string {
    return this.fullUrl(this.viewPath(viewPath));
  }
  public viewPath(viewPath: string): string {
    return this.sitePath(SiteUrls.viewPart(viewPath));
  }
  private static viewPart(viewPath: string): string {
    return `${SiteUrls.viewsPart()}/${viewPath}`;
  }

  // TODO: remove this in favor of separate 'addParams' helper?
  public viewWithDeviceTypeUrl(
    viewPath: string,
    deviceType: DataAlertDeviceType
  ): string {
    const urlBase = this.viewUrl(viewPath);
    if (deviceType === 'default') {
      return urlBase;
    }
    return urlBase + '?' + deviceTypeParam(deviceType);
  }

  public redirectToFavoriteViewUrl(viewId: string): string {
    return this.fullUrl(this.redirectToFavoriteViewPath(viewId));
  }
  public redirectToFavoriteViewPath(viewId: string): string {
    return this.sitePath(SiteUrls.redirectToFavoriteViewPart(viewId));
  }
  private static redirectToFavoriteViewPart(viewId: string): string {
    return `/redirect_to_favorite_view/${viewId}`;
  }

  public redirectToViewUrl(viewId: string): string {
    return this.fullUrl(this.redirectToViewPath(viewId));
  }
  public redirectToViewPath(viewId: string): string {
    return this.sitePath(SiteUrls.redirectToViewPart(viewId));
  }
  private static redirectToViewPart(viewId: string): string {
    return `/redirect_to_view/${viewId}`;
  }

  // Deprecated, awaiting removal as part of RemoveUsernamesFromCVURL FF removal, tfs1269522
  public redirectToCustomViewUrl(
    viewId: string,
    username: string,
    customViewName: string
  ): string {
    return this.fullUrl(
      this.redirectToCustomViewPath(viewId, username, customViewName)
    );
  }
  // Deprecated, awaiting removal as part of RemoveUsernamesFromCVURL FF removal, tfs1269522
  public redirectToCustomViewPath(
    viewId: string,
    username: string,
    customViewName: string
  ): string {
    return this.sitePath(
      SiteUrls.redirectToCustomViewPart(viewId, username, customViewName)
    );
  }
  // Deprecated, awaiting removal as part of RemoveUsernamesFromCVURL FF removal, tfs1269522
  private static redirectToCustomViewPart(
    viewId: string,
    username: string,
    customViewName: string
  ): string {
    return `/redirect_to_custom_view/${viewId}/${username}/${customViewName}`;
  }

  public redirectToCustomViewUrlWithLuid(
    viewId: string,
    customViewLuid: string,
    customViewName: string
  ): string {
    return this.fullUrl(
      this.redirectToCustomViewPathWithLuid(
        viewId,
        customViewLuid,
        customViewName
      )
    );
  }
  public redirectToCustomViewPathWithLuid(
    viewId: string,
    customViewLuid: string,
    customViewName: string
  ): string {
    return this.sitePath(
      SiteUrls.redirectToCustomViewPartWithLuid(
        viewId,
        customViewLuid,
        customViewName
      )
    );
  }
  private static redirectToCustomViewPartWithLuid(
    viewId: string,
    customViewLuid: string,
    customViewName: string
  ): string {
    return `/redirect_to_custom_view/${viewId}/${customViewLuid}/${customViewName}`;
  }

  // Viz recommendations

  // As of Dec 2018, this end-point will be only internally accessable (alpo-dev) and
  // protected by feature flag 'recommendations.viz_recommendations_enabled'
  public recommendationListUrl(): string {
    return this.fullUrl(this.recommendationListPath());
  }
  public recommendationListPath(): string {
    return this.sitePath(SiteUrls.recommendationListPart());
  }
  private static recommendationListPart(): string {
    return '/recommendations';
  }

  public recommendationUrl(vizId: string): string {
    return this.fullUrl(this.recommendationPath(vizId));
  }
  public recommendationPath(vizId: string): string {
    return this.sitePath(SiteUrls.recommendationPart(vizId));
  }
  private static recommendationPart(vizId: string): string {
    return `${SiteUrls.recommendationListPart()}/${vizId}`;
  }

  // Users

  public usersUrl(): string {
    return this.fullUrl(this.usersPath());
  }
  public usersPath(): string {
    return this.sitePath(SiteUrls.usersPart());
  }
  private static usersPart(): string {
    return '/users';
  }

  // While the user identity migration is in process, we move the users space into the users/list subspace.

  public allUsersListUrl(): string {
    return this.fullUrl(this.allUsersListPath());
  }

  public allUsersListPath(): string {
    return this.usersPath() + SiteUrls.allUsersListPart();
  }

  private static allUsersListPart(): string {
    return '/list';
  }

  // While the user identity migration is in process, we have an identity subspace for users.

  public allUserIdentitiesUrl(): string {
    return this.fullUrl(this.allUserIdentitiesPath());
  }

  public allUserIdentitiesPath(): string {
    return this.usersPath() + SiteUrls.allUserIdentitiesPart();
  }

  private static allUserIdentitiesPart(): string {
    return '/identities';
  }

  public baseUserUrl(): string {
    return this.fullUrl(this.baseUserPath());
  }
  public baseUserPath(): string {
    return this.sitePath(SiteUrls.baseUserPart());
  }
  private static baseUserPart(): string {
    return `/user`;
  }

  public userUrl(username: string, domainName: string): string {
    return this.fullUrl(this.userPath(username, domainName));
  }
  public userPath(username: string, domainName: string): string {
    return this.sitePath(SiteUrls.userPart(username, domainName));
  }
  private static userPart(username: string, domainName: string): string {
    return encodePath(`/user/${domainName}/${username}`);
  }

  public userContentUrl(username: string, domainName: string): string {
    return this.fullUrl(this.userContentPath(username, domainName));
  }
  public userContentPath(username: string, domainName: string): string {
    return this.sitePath(SiteUrls.userContentPart(username, domainName));
  }
  private static userContentPart(username: string, domainName: string): string {
    return `${SiteUrls.userPart(
      username,
      domainName
    )}${SiteUrls.contentPart()}`;
  }
  private static contentPart(): string {
    return '/content';
  }

  public userCustomViewsUrl(username: string, domainName: string): string {
    return this.fullUrl(this.userCustomViewsPath(username, domainName));
  }
  public userCustomViewsPath(username: string, domainName: string): string {
    return this.sitePath(SiteUrls.userCustomViewsPart(username, domainName));
  }
  private static userCustomViewsPart(
    username: string,
    domainName: string
  ): string {
    return `${SiteUrls.userPart(
      username,
      domainName
    )}${SiteUrls.customViewsPart()}`;
  }
  private static customViewsPart(): string {
    return '/customViews';
  }

  public userDataAlertsUrl(username: string, domainName: string): string {
    return this.fullUrl(this.userDataAlertsPath(username, domainName));
  }
  public userDataAlertsPath(username: string, domainName: string): string {
    return this.sitePath(SiteUrls.userDataAlertsPart(username, domainName));
  }
  private static userDataAlertsPart(
    username: string,
    domainName: string
  ): string {
    return `${SiteUrls.userPart(
      username,
      domainName
    )}${SiteUrls.dataAlertsPart()}`;
  }
  private static dataAlertsPart(): string {
    return '/dataAlerts';
  }

  public userSubscriptionsUrl(username: string, domainName: string): string {
    return this.fullUrl(this.userSubscriptionsPath(username, domainName));
  }
  public userSubscriptionsPath(username: string, domainName: string): string {
    return this.sitePath(SiteUrls.userSubscriptionsPart(username, domainName));
  }
  private static userSubscriptionsPart(
    username: string,
    domainName: string
  ): string {
    return `${SiteUrls.userPart(
      username,
      domainName
    )}${SiteUrls.subscriptionsPart()}`;
  }

  public userCollectionsUrl(username: string, domainName: string): string {
    return this.fullUrl(this.userCollectionsPath(username, domainName));
  }
  public userCollectionsPath(username: string, domainName: string): string {
    return this.sitePath(SiteUrls.userCollectionsPart(username, domainName));
  }
  private static userCollectionsPart(
    username: string,
    domainName: string
  ): string {
    return `${SiteUrls.userPart(
      username,
      domainName
    )}${SiteUrls.collectionsPath()}`;
  }

  public userPerformanceUrl(username: string, domainName: string): string {
    return this.fullUrl(this.userPerformancePath(username, domainName));
  }
  public userPerformancePath(username: string, domainName: string): string {
    return this.sitePath(SiteUrls.userPerformancePart(username, domainName));
  }
  private static userPerformancePart(
    username: string,
    domainName: string
  ): string {
    return `${SiteUrls.userPart(
      username,
      domainName
    )}${SiteUrls.performancePath()}`;
  }

  public userSettingsUrl(username: string, domainName: string): string {
    return this.fullUrl(this.userSettingsPath(username, domainName));
  }
  public userSettingsPath(username: string, domainName: string): string {
    return this.sitePath(SiteUrls.userSettingsPart(username, domainName));
  }
  private static userSettingsPart(
    username: string,
    domainName: string
  ): string {
    return `${SiteUrls.userPart(
      username,
      domainName
    )}${SiteUrls.settingsPart()}`;
  }

  // Legacy support user urls: projects, workbooks, views, metrics, datasources. flows
  // for supporting previously bookmarked urls. no new user subspaces should be added here
  public userProjectsUrl(username: string, domainName: string): string {
    return this.fullUrl(this.userProjectsPath(username, domainName));
  }
  public userProjectsPath(username: string, domainName: string): string {
    return this.sitePath(SiteUrls.userProjectsPart(username, domainName));
  }
  private static userProjectsPart(
    username: string,
    domainName: string
  ): string {
    return `${SiteUrls.userPart(
      username,
      domainName
    )}${SiteUrls.projectsPart()}`;
  }

  public userWorkbooksUrl(username: string, domainName: string): string {
    return this.fullUrl(this.userWorkbooksPath(username, domainName));
  }
  public userWorkbooksPath(username: string, domainName: string): string {
    return this.sitePath(SiteUrls.userWorkbooksPart(username, domainName));
  }
  private static userWorkbooksPart(
    username: string,
    domainName: string
  ): string {
    return `${SiteUrls.userPart(
      username,
      domainName
    )}${SiteUrls.workbooksPart()}`;
  }

  public userViewsUrl(username: string, domainName: string): string {
    return this.fullUrl(this.userViewsPath(username, domainName));
  }
  public userViewsPath(username: string, domainName: string): string {
    return this.sitePath(SiteUrls.userViewsPart(username, domainName));
  }
  private static userViewsPart(username: string, domainName: string): string {
    return `${SiteUrls.userPart(username, domainName)}${SiteUrls.viewsPart()}`;
  }

  public userMetricsUrl(username: string, domainName: string): string {
    return this.fullUrl(this.userMetricsPath(username, domainName));
  }
  public userMetricsPath(username: string, domainName: string): string {
    return this.sitePath(SiteUrls.userMetricsPart(username, domainName));
  }
  private static userMetricsPart(username: string, domainName: string): string {
    return `${SiteUrls.userPart(
      username,
      domainName
    )}${SiteUrls.metricsPart()}`;
  }

  public userDatasourcesUrl(username: string, domainName: string): string {
    return this.fullUrl(this.userDatasourcesPath(username, domainName));
  }
  public userDatasourcesPath(username: string, domainName: string): string {
    return this.sitePath(SiteUrls.userDatasourcesPart(username, domainName));
  }
  private static userDatasourcesPart(
    username: string,
    domainName: string
  ): string {
    return `${SiteUrls.userPart(
      username,
      domainName
    )}${SiteUrls.datasourcesPart()}`;
  }

  public userFlowsUrl(username: string, domainName: string): string {
    return this.fullUrl(this.userFlowsPath(username, domainName));
  }
  public userFlowsPath(username: string, domainName: string): string {
    return this.sitePath(SiteUrls.userFlowsPart(username, domainName));
  }
  private static userFlowsPart(username: string, domainName: string): string {
    return `${SiteUrls.userPart(username, domainName)}${SiteUrls.flowsPart()}`;
  }

  // Data Sources

  public datasourcesUrl(): string {
    return this.fullUrl(this.datasourcesPath());
  }
  public datasourcesPath(): string {
    return this.sitePath(SiteUrls.datasourcesPart());
  }
  private static datasourcesPart(): string {
    return '/datasources';
  }

  public datasourcesWithTagUrl(tag: string): string {
    return this.fullUrl(this.datasourcesWithTagPath(tag));
  }

  public datasourcesWithTagPath(tag: string): string {
    return this.sitePath(SiteUrls.datasourcesWithTagPart(tag));
  }

  private static datasourcesWithTagPart(tag: string): string {
    return `${SiteUrls.datasourcesPart()}${SiteUrls.tagPart(tag)}`;
  }

  public datasourceUrl(datasourceId: string): string {
    return this.fullUrl(this.datasourcePath(datasourceId));
  }
  public datasourcePath(datasourceId: string): string {
    return this.sitePath(SiteUrls.datasourcePart(datasourceId));
  }
  private static datasourcePart(datasourceId: string): string {
    return `${SiteUrls.datasourcesPart()}/${datasourceId}`;
  }

  public exploreDatasourceUrl(datasourceId: string): string {
    return this.fullUrl(this.exploreDatasourcePath(datasourceId));
  }
  public exploreDatasourcePath(datasourceId: string): string {
    return this.sitePath(SiteUrls.exploreDatasourcePart(datasourceId));
  }
  private static exploreDatasourcePart(datasourceId: string): string {
    return `/explore/datasource/${datasourceId}`;
  }

  public datasourcePublishresultUrl(datasourceId: string): string {
    return this.fullUrl(this.datasourcePublishresultPath(datasourceId));
  }
  public datasourcePublishresultPath(datasourceId: string): string {
    return this.sitePath(SiteUrls.datasourcePublishresultPart(datasourceId));
  }
  private static datasourcePublishresultPart(datasourceId: string): string {
    return `${SiteUrls.datasourcePart(
      datasourceId
    )}${SiteUrls.publishresultPart()}`;
  }

  public datasourceAskDataUsageAnalyticsUrl(datasourceId: string): string {
    return this.fullUrl(this.datasourceAskDataUsageAnalyticsPath(datasourceId));
  }
  public datasourceAskDataUsageAnalyticsPath(datasourceId: string): string {
    return this.sitePath(
      SiteUrls.datasourceAskDataUsageAnalyticsPart(datasourceId)
    );
  }
  private static datasourceAskDataUsageAnalyticsPart(
    datasourceId: string
  ): string {
    return `${SiteUrls.datasourcePart(
      datasourceId
    )}${SiteUrls.askDataUsageAnalyticsPart()}`;
  }
  private static askDataUsageAnalyticsPart(): string {
    return '/askDataUsageAnalytics';
  }

  public datasourceAskDataUrl(datasourceId: string): string {
    return this.fullUrl(this.datasourceAskDataPath(datasourceId));
  }
  public datasourceAskDataPath(datasourceId: string): string {
    return this.sitePath(SiteUrls.datasourceAskDataPart(datasourceId));
  }
  private static datasourceAskDataPart(datasourceId: string): string {
    return `${SiteUrls.datasourcePart(datasourceId)}${SiteUrls.askDataPart()}`;
  }
  private static askDataPart(): string {
    return '/askData';
  }

  public datasourceConnectionsUrl(datasourceId: string): string {
    return this.fullUrl(this.datasourceConnectionsPath(datasourceId));
  }
  public datasourceConnectionsPath(datasourceId: string): string {
    return this.sitePath(SiteUrls.datasourceConnectionsPart(datasourceId));
  }
  private static datasourceConnectionsPart(datasourceId: string): string {
    return `${SiteUrls.datasourcePart(
      datasourceId
    )}${SiteUrls.connectionsPart()}`;
  }

  public datasourceExtractRefreshesUrl(datasourceId: string): string {
    return this.fullUrl(this.datasourceExtractRefreshesPath(datasourceId));
  }
  public datasourceExtractRefreshesPath(datasourceId: string): string {
    return this.sitePath(SiteUrls.datasourceExtractRefreshesPart(datasourceId));
  }
  private static datasourceExtractRefreshesPart(datasourceId: string): string {
    return `${SiteUrls.datasourcePart(
      datasourceId
    )}${SiteUrls.extractRefreshesPart()}`;
  }

  public datasourceLocalExtractRefreshesUrl(datasourceId: string): string {
    return this.fullUrl(this.datasourceLocalExtractRefreshesPath(datasourceId));
  }
  public datasourceLocalExtractRefreshesPath(datasourceId: string): string {
    return this.sitePath(
      SiteUrls.datasourceLocalExtractRefreshesPart(datasourceId)
    );
  }
  private static datasourceLocalExtractRefreshesPart(
    datasourceId: string
  ): string {
    return `${SiteUrls.datasourcePart(
      datasourceId
    )}${SiteUrls.localExtractRefreshesPart()}`;
  }
  private static localExtractRefreshesPart(): string {
    return '/localExtractRefreshes';
  }

  public datasourceConnectedWorkbooksUrl(datasourceId: string): string {
    return this.fullUrl(this.datasourceConnectedWorkbooksPath(datasourceId));
  }
  public datasourceConnectedWorkbooksPath(datasourceId: string): string {
    return this.sitePath(
      SiteUrls.datasourceConnectedWorkbooksPart(datasourceId)
    );
  }
  private static datasourceConnectedWorkbooksPart(
    datasourceId: string
  ): string {
    return `${SiteUrls.datasourcePart(
      datasourceId
    )}${SiteUrls.connectedWorkbooksPart()}`;
  }
  private static connectedWorkbooksPart(): string {
    return '/connectedWorkbooks';
  }

  public datasourceDetailsUrl(datasourceId: string): string {
    return this.fullUrl(this.datasourceDetailsPath(datasourceId));
  }
  public datasourceDetailsPath(datasourceId: string): string {
    return this.sitePath(SiteUrls.datasourceDetailsPart(datasourceId));
  }
  private static datasourceDetailsPart(datasourceId: string): string {
    return `${SiteUrls.datasourcePart(datasourceId)}${SiteUrls.detailsPart()}`;
  }
  private static detailsPart(): string {
    return '/details';
  }

  public datasourceDataConnectionUrl(datasourceId: string): string {
    return this.fullUrl(this.datasourceDataConnectionPath(datasourceId));
  }
  public datasourceDataConnectionPath(datasourceId: string): string {
    return this.sitePath(SiteUrls.datasourceDataConnectionPart(datasourceId));
  }
  private static datasourceDataConnectionPart(datasourceId: string): string {
    return `${SiteUrls.datasourcePart(
      datasourceId
    )}${SiteUrls.dataConnectionPart()}`;
  }
  private static dataConnectionPart(): string {
    return '/dataConnection';
  }

  public favoriteDatasourceUrl(
    datasourceId: string,
    datasourceIdForFavoriting: string
  ): string {
    return this.fullUrl(
      this.favoriteDatasourcePath(datasourceId, datasourceIdForFavoriting)
    );
  }
  public favoriteDatasourcePath(
    datasourceId: string,
    datasourceIdForFavoriting: string
  ): string {
    return this.sitePath(
      SiteUrls.favoriteDatasourcePart(datasourceId, datasourceIdForFavoriting)
    );
  }
  private static favoriteDatasourcePart(
    datasourceId: string,
    datasourceIdForFavoriting: string
  ): string {
    return `/redirect_to_datasource/${datasourceIdForFavoriting}/${datasourceId}`;
  }

  public favoriteDatasourceDoesntExistUrl(
    datasourceIdForFavoriting: string
  ): string {
    return this.fullUrl(
      this.favoriteDatasourceDoesntExistPath(datasourceIdForFavoriting)
    );
  }
  public favoriteDatasourceDoesntExistPath(
    datasourceIdForFavoriting: string
  ): string {
    return this.sitePath(
      SiteUrls.favoriteDatasourceDoesntExistPart(datasourceIdForFavoriting)
    );
  }
  private static favoriteDatasourceDoesntExistPart(
    datasourceIdForFavoriting: string
  ): string {
    return `/redirect_to_removed_datasource/${datasourceIdForFavoriting}`;
  }

  public datasourceLineageUrl(datasourceId: string): string {
    return this.fullUrl(this.datasourceLineagePath(datasourceId));
  }
  public datasourceLineagePath(datasourceId: string): string {
    return this.sitePath(SiteUrls.datasourceLineagePart(datasourceId));
  }
  private static datasourceLineagePart(datasourceId: string): string {
    return `${SiteUrls.datasourcePart(datasourceId)}${SiteUrls.lineagePart()}`;
  }

  public datasourceLineageFieldsUrl(datasourceId: string): string {
    return this.fullUrl(this.datasourceLineageFieldsPath(datasourceId));
  }
  public datasourceLineageFieldsPath(datasourceId: string): string {
    return this.sitePath(SiteUrls.datasourceLineageFieldsPart(datasourceId));
  }
  private static datasourceLineageFieldsPart(datasourceId: string): string {
    return `${SiteUrls.datasourceLineagePart(
      datasourceId
    )}${SiteUrls.fieldsPart()}`;
  }

  public datasourceLineageFieldUrl(
    datasourceId: string,
    fieldId: string
  ): string {
    return this.fullUrl(this.datasourceLineageFieldPath(datasourceId, fieldId));
  }
  public datasourceLineageFieldPath(
    datasourceId: string,
    fieldId: string
  ): string {
    return this.sitePath(
      SiteUrls.datasourceLineageFieldPart(datasourceId, fieldId)
    );
  }
  private static datasourceLineageFieldPart(
    datasourceId: string,
    fieldId: string
  ): string {
    return `${SiteUrls.datasourceLineagePart(
      datasourceId
    )}${SiteUrls.fieldsPart()}/${fieldId}`;
  }

  public datasourceLineageUpstreamDatabasesUrl(datasourceId: string): string {
    return this.fullUrl(
      this.datasourceLineageUpstreamDatabasesPath(datasourceId)
    );
  }
  public datasourceLineageUpstreamDatabasesPath(datasourceId: string): string {
    return this.sitePath(
      SiteUrls.datasourceLineageUpstreamDatabasesPart(datasourceId)
    );
  }
  private static datasourceLineageUpstreamDatabasesPart(
    datasourceId: string
  ): string {
    return `${SiteUrls.datasourceLineagePart(
      datasourceId
    )}${SiteUrls.upstreamPart()}${SiteUrls.databasesPart()}`;
  }

  public datasourceLineageUpstreamTablesUrl(datasourceId: string): string {
    return this.fullUrl(this.datasourceLineageUpstreamTablesPath(datasourceId));
  }
  public datasourceLineageUpstreamTablesPath(datasourceId: string): string {
    return this.sitePath(
      SiteUrls.datasourceLineageUpstreamTablesPart(datasourceId)
    );
  }
  private static datasourceLineageUpstreamTablesPart(
    datasourceId: string
  ): string {
    return `${SiteUrls.datasourceLineagePart(
      datasourceId
    )}${SiteUrls.upstreamPart()}${SiteUrls.tablesPart()}`;
  }

  public datasourceLineageUpstreamVirtualConnectionsUrl(datasourceId: string): string {
    return this.fullUrl(this.datasourceLineageUpstreamVirtualConnectionsPath(datasourceId));
  }
  public datasourceLineageUpstreamVirtualConnectionsPath(datasourceId: string): string {
    return this.sitePath(
      SiteUrls.datasourceLineageUpstreamVirtualConnectionsPart(datasourceId)
    );
  }
  private static datasourceLineageUpstreamVirtualConnectionsPart(
    datasourceId: string
  ): string {
    return `${SiteUrls.datasourceLineagePart(
      datasourceId
    )}${SiteUrls.upstreamPart()}${SiteUrls.virtualConnectionsPart()}`;
  }

  public datasourceLineageUpstreamVirtualConnectionTablesUrl(datasourceId: string): string {
    return this.fullUrl(this.datasourceLineageUpstreamVirtualConnectionTablesPath(datasourceId));
  }
  public datasourceLineageUpstreamVirtualConnectionTablesPath(datasourceId: string): string {
    return this.sitePath(
      SiteUrls.datasourceLineageUpstreamVirtualConnectionTablesPart(datasourceId)
    );
  }
  private static datasourceLineageUpstreamVirtualConnectionTablesPart(
    datasourceId: string
  ): string {
    return `${SiteUrls.datasourceLineagePart(
      datasourceId
    )}${SiteUrls.upstreamPart()}${SiteUrls.virtualConnectionTablesPart()}`;
  }

  public datasourceLineageUpstreamFlowsUrl(datasourceId: string): string {
    return this.fullUrl(this.datasourceLineageUpstreamFlowsPath(datasourceId));
  }
  public datasourceLineageUpstreamFlowsPath(datasourceId: string): string {
    return this.sitePath(
      SiteUrls.datasourceLineageUpstreamFlowsPart(datasourceId)
    );
  }
  private static datasourceLineageUpstreamFlowsPart(
    datasourceId: string
  ): string {
    return `${SiteUrls.datasourceLineagePart(
      datasourceId
    )}${SiteUrls.upstreamPart()}${SiteUrls.flowsPart()}`;
  }

  public datasourceLineageUpstreamDatasourcesUrl(datasourceId: string): string {
    return this.fullUrl(
      this.datasourceLineageUpstreamDatasourcesPath(datasourceId)
    );
  }
  public datasourceLineageUpstreamDatasourcesPath(
    datasourceId: string
  ): string {
    return this.sitePath(
      SiteUrls.datasourceLineageUpstreamDatasourcesPart(datasourceId)
    );
  }
  private static datasourceLineageUpstreamDatasourcesPart(
    datasourceId: string
  ): string {
    return `${SiteUrls.datasourceLineagePart(
      datasourceId
    )}${SiteUrls.upstreamPart()}${SiteUrls.datasourcesPart()}`;
  }

  public datasourceLineageDownstreamDatabasesUrl(datasourceId: string): string {
    return this.fullUrl(
      this.datasourceLineageDownstreamDatabasesPath(datasourceId)
    );
  }
  public datasourceLineageDownstreamDatabasesPath(
    datasourceId: string
  ): string {
    return this.sitePath(
      SiteUrls.datasourceLineageDownstreamDatabasesPart(datasourceId)
    );
  }
  private static datasourceLineageDownstreamDatabasesPart(
    datasourceId: string
  ): string {
    return `${SiteUrls.datasourceLineagePart(
      datasourceId
    )}${SiteUrls.downstreamPart()}${SiteUrls.databasesPart()}`;
  }

  public datasourceLineageDownstreamTablesUrl(datasourceId: string): string {
    return this.fullUrl(
      this.datasourceLineageDownstreamTablesPath(datasourceId)
    );
  }
  public datasourceLineageDownstreamTablesPath(datasourceId: string): string {
    return this.sitePath(
      SiteUrls.datasourceLineageDownstreamTablesPart(datasourceId)
    );
  }
  private static datasourceLineageDownstreamTablesPart(
    datasourceId: string
  ): string {
    return `${SiteUrls.datasourceLineagePart(
      datasourceId
    )}${SiteUrls.downstreamPart()}${SiteUrls.tablesPart()}`;
  }

  public datasourceLineageDownstreamFlowsUrl(datasourceId: string): string {
    return this.fullUrl(
      this.datasourceLineageDownstreamFlowsPath(datasourceId)
    );
  }
  public datasourceLineageDownstreamFlowsPath(datasourceId: string): string {
    return this.sitePath(
      SiteUrls.datasourceLineageDownstreamFlowsPart(datasourceId)
    );
  }
  private static datasourceLineageDownstreamFlowsPart(
    datasourceId: string
  ): string {
    return `${SiteUrls.datasourceLineagePart(
      datasourceId
    )}${SiteUrls.downstreamPart()}${SiteUrls.flowsPart()}`;
  }

  public datasourceLineageDownstreamDatasourcesUrl(
    datasourceId: string
  ): string {
    return this.fullUrl(
      this.datasourceLineageDownstreamDatasourcesPath(datasourceId)
    );
  }
  public datasourceLineageDownstreamDatasourcesPath(
    datasourceId: string
  ): string {
    return this.sitePath(
      SiteUrls.datasourceLineageDownstreamDatasourcesPart(datasourceId)
    );
  }
  private static datasourceLineageDownstreamDatasourcesPart(
    datasourceId: string
  ): string {
    return `${SiteUrls.datasourceLineagePart(
      datasourceId
    )}${SiteUrls.downstreamPart()}${SiteUrls.datasourcesPart()}`;
  }

  public datasourceLineageDownstreamLensesUrl(
    datasourceId: string
  ): string {
    return this.fullUrl(
      this.datasourceLineageDownstreamLensesPath(datasourceId)
    );
  }
  public datasourceLineageDownstreamLensesPath(
    datasourceId: string
  ): string {
    return this.sitePath(
      SiteUrls.datasourceLineageDownstreamLensesPart(datasourceId)
    );
  }
  private static datasourceLineageDownstreamLensesPart(
    datasourceId: string
  ): string {
    return `${SiteUrls.datasourceLineagePart(
      datasourceId
    )}${SiteUrls.downstreamPart()}${SiteUrls.lensesPart()}`;
  }

  public datasourceLineageDownstreamWorkbooksUrl(datasourceId: string): string {
    return this.fullUrl(
      this.datasourceLineageDownstreamWorkbooksPath(datasourceId)
    );
  }
  public datasourceLineageDownstreamWorkbooksPath(
    datasourceId: string
  ): string {
    return this.sitePath(
      SiteUrls.datasourceLineageDownstreamWorkbooksPart(datasourceId)
    );
  }
  private static datasourceLineageDownstreamWorkbooksPart(
    datasourceId: string
  ): string {
    return `${SiteUrls.datasourceLineagePart(
      datasourceId
    )}${SiteUrls.downstreamPart()}${SiteUrls.workbooksPart()}`;
  }

  public datasourceLineageDownstreamDashboardsUrl(
    datasourceId: string
  ): string {
    return this.fullUrl(
      this.datasourceLineageDownstreamDashboardsPath(datasourceId)
    );
  }
  public datasourceLineageDownstreamDashboardsPath(
    datasourceId: string
  ): string {
    return this.sitePath(
      SiteUrls.datasourceLineageDownstreamDashboardsPart(datasourceId)
    );
  }
  private static datasourceLineageDownstreamDashboardsPart(
    datasourceId: string
  ): string {
    return `${SiteUrls.datasourceLineagePart(
      datasourceId
    )}${SiteUrls.downstreamPart()}${SiteUrls.dashboardsPart()}`;
  }

  public datasourceLineageDownstreamSheetsUrl(datasourceId: string): string {
    return this.fullUrl(
      this.datasourceLineageDownstreamSheetsPath(datasourceId)
    );
  }
  public datasourceLineageDownstreamSheetsPath(datasourceId: string): string {
    return this.sitePath(
      SiteUrls.datasourceLineageDownstreamSheetsPart(datasourceId)
    );
  }
  private static datasourceLineageDownstreamSheetsPart(
    datasourceId: string
  ): string {
    return `${SiteUrls.datasourceLineagePart(
      datasourceId
    )}${SiteUrls.downstreamPart()}${SiteUrls.sheetsPart()}`;
  }

  public datasourceLineageDownstreaMetricsUrl(datasourceId: string): string {
    return this.fullUrl(
      this.datasourceLineageDownstreamMetricsPath(datasourceId)
    );
  }
  public datasourceLineageDownstreamMetricsPath(datasourceId: string): string {
    return this.sitePath(
      SiteUrls.datasourceLineageDownstreamMetricsPart(datasourceId)
    );
  }
  private static datasourceLineageDownstreamMetricsPart(
    datasourceId: string
  ): string {
    return `${SiteUrls.datasourceLineagePart(
      datasourceId
    )}${SiteUrls.downstreamPart()}${SiteUrls.lineageMetricsPart()}`;
  }

  public datasourceLineageDownstreamOwnersUrl(datasourceId: string): string {
    return this.fullUrl(
      this.datasourceLineageDownstreamOwnersPath(datasourceId)
    );
  }
  public datasourceLineageDownstreamOwnersPath(datasourceId: string): string {
    return this.sitePath(
      SiteUrls.datasourceLineageDownstreamOwnersPart(datasourceId)
    );
  }
  private static datasourceLineageDownstreamOwnersPart(
    datasourceId: string
  ): string {
    return `${SiteUrls.datasourceLineagePart(
      datasourceId
    )}${SiteUrls.downstreamPart()}${SiteUrls.ownersPart()}`;
  }

  // Groups

  public groupsUrl(): string {
    return this.fullUrl(this.groupsPath());
  }
  public groupsPath(): string {
    return this.sitePath(SiteUrls.groupsPart());
  }
  private static groupsPart(): string {
    return '/groups';
  }

  public groupUrl(groupId: string): string {
    return this.fullUrl(this.groupPath(groupId));
  }
  public groupPath(groupId: string): string {
    return this.sitePath(SiteUrls.groupPart(groupId));
  }
  private static groupPart(groupId: string): string {
    return `${SiteUrls.groupsPart()}/${groupId}`;
  }

  public groupUsersUrl(groupId: string): string {
    return this.fullUrl(this.groupUsersPath(groupId));
  }
  public groupUsersPath(groupId: string): string {
    return this.sitePath(SiteUrls.groupUsersPart(groupId));
  }
  private static groupUsersPart(groupId: string): string {
    return `${SiteUrls.groupPart(groupId)}${SiteUrls.usersPart()}`;
  }

  public groupDetailsUrl(groupId: string): string {
    return this.fullUrl(this.groupDetailsPath(groupId));
  }
  public groupDetailsPath(groupId: string): string {
    return this.sitePath(SiteUrls.groupDetailsPart(groupId));
  }
  private static groupDetailsPart(groupId: string): string {
    return `${SiteUrls.groupPart(groupId)}${SiteUrls.detailsPart()}`;
  }

  // Broadcasts

  public broadcastsUrl(): string {
    return this.fullUrl(this.broadcastsPath());
  }
  public broadcastsPath(): string {
    return this.sitePath(SiteUrls.broadcastsPart());
  }
  private static broadcastsPart(): string {
    return '/broadcasts';
  }

  // Schedules

  public schedulesUrl(): string {
    return this.fullUrl(this.schedulesPath());
  }
  public schedulesPath(): string {
    return this.sitePath(SiteUrls.schedulesPart());
  }
  private static schedulesPart(): string {
    return '/schedules';
  }

  public scheduleUrl(scheduleId: string): string {
    return this.fullUrl(this.schedulePath(scheduleId));
  }
  public schedulePath(scheduleId: string): string {
    return this.sitePath(SiteUrls.schedulePart(scheduleId));
  }
  private static schedulePart(scheduleId: string): string {
    return `${SiteUrls.schedulesPart()}/${scheduleId}`;
  }

  // public scheduleExtractRefreshesUrl(scheduleId: string): string {
  //   return this.scheduleUrl(scheduleId) + '/extractRefreshes';
  // }

  public scheduleExtractRefreshesUrl(scheduleId: string): string {
    return this.fullUrl(this.scheduleExtractRefreshesPath(scheduleId));
  }
  public scheduleExtractRefreshesPath(scheduleId: string): string {
    return this.sitePath(SiteUrls.scheduleExtractRefreshesPart(scheduleId));
  }
  private static scheduleExtractRefreshesPart(scheduleId: string): string {
    return `${SiteUrls.schedulePart(
      scheduleId
    )}${SiteUrls.extractRefreshesPart()}`;
  }
  private static extractRefreshesPart(): string {
    return '/extractRefreshes';
  }

  public scheduleFlowsUrl(scheduleId: string): string {
    return this.fullUrl(this.scheduleFlowsPath(scheduleId));
  }
  public scheduleFlowsPath(scheduleId: string): string {
    return this.sitePath(SiteUrls.scheduleFlowsPart(scheduleId));
  }
  private static scheduleFlowsPart(scheduleId: string): string {
    return `${SiteUrls.schedulePart(scheduleId)}${SiteUrls.flowsPart()}`;
  }

  public scheduleSubscriptionsUrl(scheduleId: string): string {
    return this.fullUrl(this.scheduleSubscriptionsPath(scheduleId));
  }
  public scheduleSubscriptionsPath(scheduleId: string): string {
    return this.sitePath(SiteUrls.scheduleSubscriptionsPart(scheduleId));
  }
  private static scheduleSubscriptionsPart(scheduleId: string): string {
    return `${SiteUrls.schedulePart(
      scheduleId
    )}${SiteUrls.subscriptionsPart()}`;
  }
  private static subscriptionsPart(): string {
    return '/subscriptions';
  }

  public scheduleDataAccelerationUrl(scheduleId: string): string {
    return this.fullUrl(this.scheduleDataAccelerationPath(scheduleId));
  }
  public scheduleDataAccelerationPath(scheduleId: string): string {
    return this.sitePath(SiteUrls.scheduleDataAccelerationPart(scheduleId));
  }
  private static scheduleDataAccelerationPart(scheduleId: string): string {
    return `${SiteUrls.schedulePart(
      scheduleId
    )}${SiteUrls.dataAccelerationPart()}`;
  }

  public scheduleDetailsUrl(scheduleId: string): string {
    return this.fullUrl(this.scheduleDetailsPath(scheduleId));
  }
  public scheduleDetailsPath(scheduleId: string): string {
    return this.sitePath(SiteUrls.scheduleDetailsPart(scheduleId));
  }
  private static scheduleDetailsPart(scheduleId: string): string {
    return `${SiteUrls.schedulePart(scheduleId)}${SiteUrls.detailsPart()}`;
  }

  // Jobs

  public jobsUrl(): string {
    return this.fullUrl(this.jobsPath());
  }
  public jobsPath(): string {
    return this.sitePath(SiteUrls.jobsPart());
  }
  private static jobsPart(): string {
    return '/jobs';
  }

  // Labels

  public labelsUrl(): string {
    return this.fullUrl(this.labelsPath());
  }
  public labelsPath(): string {
    return this.sitePath(SiteUrls.labelsPart());
  }
  private static labelsPart(): string {
    return '/labels';
  }

  // Favorites

  public favoritesUrl(): string {
    return this.fullUrl(this.favoritesPath());
  }
  public favoritesPath(): string {
    return this.sitePath(SiteUrls.favoritesPart());
  }
  private static favoritesPart(): string {
    return '/favorites';
  }

  // Recents

  public recentsUrl(): string {
    return this.fullUrl(this.recentsPath());
  }
  public recentsPath(): string {
    return this.sitePath(SiteUrls.recentsPart());
  }
  private static recentsPart(): string {
    return '/recents';
  }

  // search

  public searchUrl(): string {
    return this.fullUrl(this.searchPath());
  }
  public searchPath(): string {
    return this.sitePath(SiteUrls.searchPart());
  }
  private static searchPart(): string {
    return '/search';
  }

  public searchViewsUrl(): string {
    return this.fullUrl(this.searchViewsPath());
  }
  public searchViewsPath(): string {
    return this.sitePath(SiteUrls.searchViewsPart());
  }
  private static searchViewsPart(): string {
    return `${SiteUrls.searchPart()}${SiteUrls.viewsPart()}`;
  }

  public searchMetricsUrl(): string {
    return this.fullUrl(this.searchMetricsPath());
  }
  public searchMetricsPath(): string {
    return this.sitePath(SiteUrls.searchMetricsPart());
  }
  private static searchMetricsPart(): string {
    return `${SiteUrls.searchPart()}${SiteUrls.metricsPart()}`;
  }

  public searchWorkbooksUrl(): string {
    return this.fullUrl(this.searchWorkbooksPath());
  }
  public searchWorkbooksPath(): string {
    return this.sitePath(SiteUrls.searchWorkbooksPart());
  }
  private static searchWorkbooksPart(): string {
    return `${SiteUrls.searchPart()}${SiteUrls.workbooksPart()}`;
  }

  public searchDatasourcesUrl(): string {
    return this.fullUrl(this.searchDatasourcesPath());
  }
  public searchDatasourcesPath(): string {
    return this.sitePath(SiteUrls.searchDatasourcesPart());
  }
  private static searchDatasourcesPart(): string {
    return `${SiteUrls.searchPart()}${SiteUrls.datasourcesPart()}`;
  }

  public searchProjectsUrl(): string {
    return this.fullUrl(this.searchProjectsPath());
  }
  public searchProjectsPath(): string {
    return this.sitePath(SiteUrls.searchProjectsPart());
  }
  private static searchProjectsPart(): string {
    return `${SiteUrls.searchPart()}${SiteUrls.projectsPart()}`;
  }

  public searchFlowsUrl(): string {
    return this.fullUrl(this.searchFlowsPath());
  }
  public searchFlowsPath(): string {
    return this.sitePath(SiteUrls.searchFlowsPart());
  }
  private static searchFlowsPart(): string {
    return `${SiteUrls.searchPart()}${SiteUrls.flowsPart()}`;
  }

  public searchTablesUrl(): string {
    return this.fullUrl(this.searchTablesPath());
  }
  public searchTablesPath(): string {
    return this.sitePath(SiteUrls.searchTablesPart());
  }
  private static searchTablesPart(): string {
    return `${SiteUrls.searchPart()}${SiteUrls.tablesPart()}`;
  }

  public searchDatabasesUrl(): string {
    return this.fullUrl(this.searchDatabasesPath());
  }
  public searchDatabasesPath(): string {
    return this.sitePath(SiteUrls.searchDatabasesPart());
  }
  private static searchDatabasesPart(): string {
    return `${SiteUrls.searchPart()}${SiteUrls.databasesPart()}`;
  }

  public searchDataRolesUrl(): string {
    return this.fullUrl(this.searchDataRolesPath());
  }
  public searchDataRolesPath(): string {
    return this.sitePath(SiteUrls.searchDataRolesPart());
  }
  private static searchDataRolesPart(): string {
    return `${SiteUrls.searchPart()}${SiteUrls.dataRolesPart()}`;
  }

  public searchLensesUrl(): string {
    return this.fullUrl(this.searchLensesPath());
  }
  public searchLensesPath(): string {
    return this.sitePath(SiteUrls.searchLensesPart());
  }
  private static searchLensesPart(): string {
    return `${SiteUrls.searchPart()}${SiteUrls.lensesPart()}`;
  }

  public searchCollectionsUrl(): string {
    return this.fullUrl(this.searchCollectionsPath());
  }
  public searchCollectionsPath(): string {
    return this.sitePath(SiteUrls.searchCollectionsPart());
  }
  private static searchCollectionsPart(): string {
    return `${SiteUrls.searchPart()}${SiteUrls.collectionsPath()}`;
  }

  public searchUsersUrl(): string {
    return this.fullUrl(this.searchUsersPath());
  }
  public searchUsersPath(): string {
    return this.sitePath(SiteUrls.searchUsersPart());
  }
  private static searchUsersPart(): string {
    return `${SiteUrls.searchPart()}${SiteUrls.usersPart()}`;
  }

  public searchVirtualConnectionsUrl(): string {
    return this.fullUrl(this.searchVirtualConnectionsPath());
  }
  public searchVirtualConnectionsPath(): string {
    return this.sitePath(SiteUrls.searchVirtualConnectionsPart());
  }
  private static searchVirtualConnectionsPart(): string {
    return `${SiteUrls.searchPart()}${SiteUrls.virtualConnectionsPart()}`;
  }

  public searchVirtualConnectionTablesUrl(): string {
    return this.fullUrl(this.searchVirtualConnectionTablesPath());
  }
  public searchVirtualConnectionTablesPath(): string {
    return this.sitePath(SiteUrls.searchVirtualConnectionTablesPart());
  }
  private static searchVirtualConnectionTablesPart(): string {
    return `${SiteUrls.searchPart()}${SiteUrls.virtualConnectionTablesPart()}`;
  }

  public searchPredictiveModelsUrl(): string {
    return this.fullUrl(this.searchPredictiveModelsPath());
  }
  public searchPredictiveModelsPath(): string {
    return this.sitePath(SiteUrls.searchPredictiveModelsPart());
  }
  private static searchPredictiveModelsPart(): string {
    return `${SiteUrls.searchPart()}${SiteUrls.predictiveModelsPart()}`;
  }


  // Others

  public settingsUrl(): string {
    return this.fullUrl(this.settingsPath());
  }
  public settingsPath(): string {
    return this.sitePath(SiteUrls.settingsPart());
  }
  private static settingsPart(): string {
    return '/settings';
  }

  public settingsAuthenticationUrl(): string {
    return this.fullUrl(this.settingsAuthenticationPath());
  }
  public settingsAuthenticationPath(): string {
    return this.sitePath(SiteUrls.authenticationPart());
  }
  private static authenticationPart(): string {
    return '/authentication';
  }

  public settingsBridgeUrl(): string {
    return this.fullUrl(this.settingsBridgePath());
  }
  public settingsBridgePath(): string {
    return this.sitePath(SiteUrls.bridgePart());
  }
  private static bridgePart(): string {
    return '/bridge';
  }

  public settingsExtensionsUrl(): string {
    return this.fullUrl(this.settingsExtensionsPath());
  }
  public settingsExtensionsPath(): string {
    return this.sitePath(SiteUrls.extensionsPart());
  }
  private static extensionsPart(): string {
    return '/extensions';
  }

  public settingsIntegrationsUrl(): string {
    return this.fullUrl(this.settingsIntegrationsPath());
  }
  public settingsIntegrationsPath(): string {
    return this.sitePath(SiteUrls.integrationsPart());
  }
  private static integrationsPart(): string {
    return '/integrations';
  }

  public settingsConnectedApplicationsUrl(): string {
    return this.fullUrl(this.settingsConnectedApplicationsPath());
  }
  public settingsConnectedApplicationsPath(): string {
    return this.sitePath(SiteUrls.connectedApplicationsPart());
  }
  private static connectedApplicationsPart(): string {
    return '/connectedApplications';
  }
  private static directTrustPart(): string {
    return '/directTrust';
  }
  private static externalAuthorizationServersPart(): string {
    return '/externalAuthorizationServers';
  }

  public settingsMobileUrl(): string {
    return this.fullUrl(this.settingsMobilePath());
  }
  public settingsMobilePath(): string {
    return this.sitePath(SiteUrls.MobilePart());
  }
  private static MobilePart(): string {
    return '/mobile';
  }

  public statusUrl(): string {
    return this.fullUrl(this.statusPath());
  }
  public statusPath(): string {
    return this.sitePath(SiteUrls.statusPart());
  }
  private static statusPart(): string {
    return '/status';
  }

  // Tasks

  public tasksUrl(): string {
    return this.fullUrl(this.tasksPath());
  }
  public tasksPath(): string {
    return this.sitePath(SiteUrls.tasksPart());
  }
  private static tasksPart(): string {
    return '/tasks';
  }

  public tasksExtractRefreshesUrl(): string {
    return this.fullUrl(this.tasksExtractRefreshesPath());
  }
  public tasksExtractRefreshesPath(): string {
    return this.sitePath(SiteUrls.tasksExtractRefreshesPart());
  }
  private static tasksExtractRefreshesPart(): string {
    return `${SiteUrls.tasksPart()}${SiteUrls.extractRefreshesPart()}`;
  }

  public tasksFlowsUrl(): string {
    return this.fullUrl(this.tasksFlowsPath());
  }
  public tasksFlowsPath(): string {
    return this.sitePath(SiteUrls.tasksFlowsPart());
  }
  private static tasksFlowsPart(): string {
    return `${SiteUrls.tasksPart()}${SiteUrls.flowsPart()}`;
  }

  public tasksSubscriptionsUrl(): string {
    return this.fullUrl(this.tasksSubscriptionsPath());
  }
  public tasksSubscriptionsPath(): string {
    return this.sitePath(SiteUrls.tasksSubscriptionsPart());
  }
  private static tasksSubscriptionsPart(): string {
    return `${SiteUrls.tasksPart()}${SiteUrls.subscriptionsPart()}`;
  }

  public tasksDataAlertsUrl(): string {
    return this.fullUrl(this.tasksDataAlertsPath());
  }
  public tasksDataAlertsPath(): string {
    return this.sitePath(SiteUrls.tasksDataAlertsPart());
  }
  private static tasksDataAlertsPart(): string {
    return `${SiteUrls.tasksPart()}${SiteUrls.dataAlertsPart()}`;
  }

  public tasksDataAccelerationUrl(): string {
    return this.fullUrl(this.tasksDataAccelerationPath());
  }
  public tasksDataAccelerationPath(): string {
    return this.sitePath(SiteUrls.tasksDataAccelerationPart());
  }
  private static tasksDataAccelerationPart(): string {
    return `${SiteUrls.tasksPart()}${SiteUrls.dataAccelerationPart()}`;
  }
  private static dataAccelerationPart(): string {
    return '/dataAcceleration';
  }

  public tasksLinkedTasksUrl(): string {
    return this.fullUrl(this.tasksLinkedTasksPath());
  }
  public tasksLinkedTasksPath(): string {
    return this.sitePath(SiteUrls.tasksLinkedTasksPart());
  }
  private static tasksLinkedTasksPart(): string {
    return `${SiteUrls.tasksPart()}${SiteUrls.linkedTasksPart()}`;
  }
  private static linkedTasksPart(): string {
    return '/linkedTasks';
  }

  // Home

  public homeUrl(): string {
    return this.fullUrl(this.homePath());
  }
  public homePath(): string {
    return this.sitePath(SiteUrls.homePart());
  }
  private static homePart(): string {
    return '/home';
  }

  // Sandbox
  public sandboxUrl(): string {
    return this.fullUrl(this.sandboxPath());
  }
  public sandboxPath(): string {
    return this.sitePath(SiteUrls.sandboxPart());
  }
  private static sandboxPart(): string {
    return '/sandbox';
  }

  // Metrics
  public metricsUrl(): string {
    return this.fullUrl(this.metricsPath());
  }
  public metricsPath(): string {
    return this.sitePath(SiteUrls.metricsPart());
  }
  private static metricsPart(): string {
    return '/metrics';
  }

  public metricUrl(metricId: string): string {
    return this.fullUrl(this.metricPath(metricId));
  }
  public metricPath(metricId: string): string {
    return this.sitePath(SiteUrls.metricPart(metricId));
  }
  private static metricPart(metricId: string): string {
    return `${SiteUrls.metricsPart()}/${metricId}`;
  }

  public favoriteMetricUrl(metricId: string): string {
    return this.redirectToMetricUrl(metricId);
  }

  public redirectToMetricUrl(metricId: string): string {
    return this.fullUrl(this.redirectToMetricPath(metricId));
  }
  public redirectToMetricPath(metricId: string): string {
    return this.sitePath(SiteUrls.redirectToMetricPart(metricId));
  }
  private static redirectToMetricPart(metricId: string): string {
    return `/redirect_to_metric/${metricId}`;
  }

  // Site Status Vizzes
  public siteStatusVizUrl(sheetUrlId: string): string {
    return this.fullUrl(this.siteStatusVizPath(sheetUrlId));
  }
  public siteStatusVizPath(sheetUrlId: string): string {
    return this.sitePath(SiteUrls.siteStatusVizPart(sheetUrlId));
  }
  private static siteStatusVizPart(sheetUrlId: string): string {
    return `/analysis/${sheetUrlId}`;
  }

  // Workbook Revision History
  public workbookRevisionHistoryUrl(params: {
    workbookUrlId: string;
    revisionNumber: string;
  }): string {
    return this.fullUrl(this.workbookRevisionHistoryPath(params));
  }
  public workbookRevisionHistoryPath(params: {
    workbookUrlId: string;
    revisionNumber: string;
  }): string {
    return this.sitePath(SiteUrls.workbookRevisionHistoryPart(params));
  }
  private static workbookRevisionHistoryPart(params: {
    workbookUrlId: string;
    revisionNumber: string;
  }): string {
    return `${SiteUrls.workbooksPart()}/${params.workbookUrlId}/history/${params.revisionNumber
      }`;
  }

  public workbookRevisionHistorySpecificSheetUrl(params: {
    workbookUrlId: string;
    sheetUrlId: string;
    revisionNumber: string;
  }): string {
    return this.fullUrl(this.workbookRevisionHistorySpecificSheetPath(params));
  }
  public workbookRevisionHistorySpecificSheetPath(params: {
    workbookUrlId: string;
    sheetUrlId: string;
    revisionNumber: string;
  }): string {
    return this.sitePath(
      SiteUrls.workbookRevisionHistorySpecificSheetPart(params)
    );
  }
  private static workbookRevisionHistorySpecificSheetPart(params: {
    workbookUrlId: string;
    sheetUrlId: string;
    revisionNumber: string;
  }): string {
    return `${SiteUrls.workbooksPart()}/${params.workbookUrlId}/${params.sheetUrlId
      }/history/${params.revisionNumber}`;
  }

  public addUserToDataAlertUrl(dataAlertId: string): string {
    return this.fullUrl(this.addUserToDataAlertPath(dataAlertId));
  }

  public addUserToDataAlertPath(dataAlertId: string): string {
    return this.sitePath(SiteUrls.addUserToDataAlertPart(dataAlertId));
  }

  private static addUserToDataAlertPart(dataAlertId: string): string {
    return `/dataAlert/${dataAlertId}/addUser/`;
  }

  public removeUserFromDataAlertUrl(dataAlertId: string): string {
    return this.fullUrl(this.removeUserFromDataAlertPath(dataAlertId));
  }

  public removeUserFromDataAlertPath(dataAlertId: string): string {
    return this.sitePath(SiteUrls.removeUserFromDataAlertPart(dataAlertId));
  }

  private static removeUserFromDataAlertPart(dataAlertId: string): string {
    return `/dataAlert/${dataAlertId}/removeUser/`;
  }

  public dataAlertPath(): string {
    return this.sitePath(SiteUrls.dataAlertPart());
  }

  private static dataAlertPart(): string {
    return '/dataAlert';
  }

  // Collections
  public collectionsUrl(): string {
    return this.fullUrl(this.collectionsPath());
  }
  public collectionsPath(): string {
    return this.sitePath(SiteUrls.collectionsPath());
  }
  private static collectionsPath(): string {
    return '/collections';
  }
  private static performancePath(): string {
    return '/performance';
  }

  public collectionUrl(collectionLuid: string): string {
    return this.fullUrl(this.collectionPath(collectionLuid));
  }
  public collectionPath(collectionLuid: string): string {
    return this.sitePath(SiteUrls.collectionPart(collectionLuid));
  }
  private static collectionPart(collectionLuid: string): string {
    return `${SiteUrls.collectionsPath()}/${collectionLuid}`;
  }

  public redirectToCollectionUrl(
    collectionId: string,
    collectionLuid: string
  ): string {
    return this.fullUrl(
      this.redirectToCollectionPath(collectionId, collectionLuid)
    );
  }
  public redirectToCollectionPath(
    collectionId: string,
    collectionLuid: string
  ): string {
    return this.sitePath(
      SiteUrls.redirectToCollectionPart(collectionId, collectionLuid)
    );
  }
  private static redirectToCollectionPart(
    collectionId: string,
    collectionLuid: string
  ): string {
    return `/redirect_to_collection/${collectionId}/${collectionLuid}`;
  }

  public allCollectionsUrl(): string {
    return this.fullUrl(this.allCollectionsPath());
  }

  public allCollectionsPath(): string {
    return this.sitePath(SiteUrls.allCollectionsPart());
  }

  private static allCollectionsPart(): string {
    return `${this.collectionsPath()}/allCollections`;
  }

  public myCollectionsUrl(): string {
    return this.fullUrl(this.myCollectionsPath());
  }

  public myCollectionsPath(): string {
    return this.sitePath(SiteUrls.myCollectionsPart());
  }

  private static myCollectionsPart(): string {
    return `${this.collectionsPath()}/myCollections`;
  }

  // VizClient

  // Note that VizClient links do not preserve URL query parameters.

  private vizClientSitePath(path: string): string {
    return `${this.vizClientSitePrefix}${path}`;
  }

  private static makeVizClientSitePrefix(siteUrlName: string): string {
    return siteUrlName && siteUrlName.length > 0 ? `/t/${siteUrlName}` : '';
  }

  public newWorkbookUrl(projectName?: string): string {
    return this.newWorkbookUrlFromAuxiliaryId(this.generateUniqueAuxiliaryId(), projectName);
  }

  public newWorkbookFromDatabase(databaseId: string): string {
    return `${this.newWorkbookUrl()}?:graphQLDatabaseId=${databaseId}`;
  }

  public newWorkbookFromTable(tableId: string): string {
    return `${this.newWorkbookUrl()}?:graphQLTableId=${tableId}`;
  }

  public unpublishedWorkbookUrl(auxiliaryId: string, projectName?: string): string {
    return this.newWorkbookUrlFromAuxiliaryId(auxiliaryId, projectName);
  }

  private newWorkbookUrlFromAuxiliaryId(auxiliaryId: string, projectName?: string): string {
    return this.vizClientSitePath(
      `/newWorkbook/${this.vizClientProjectPart(projectName)}${auxiliaryId}`
    );
  }

  private vizClientProjectPart(projectName?: string): string {
    return projectName && projectName.length > 0 ? `p/${projectName}/` : '';
  }

  // Shared with me
  public sharedWithMeUrl(): string {
    return this.fullUrl(this.sharedWithMePath());
  }

  public sharedWithMePath(): string {
    return this.sitePath(SiteUrls.sharedWithMePart());
  }

  private static sharedWithMePart(): string {
    return '/sharedWithMe';
  }

  // Personal Space
  public personalSpaceUrl(): string {
    return this.fullUrl(this.personalSpacePath());
  }

  public personalSpacePath(): string {
    return this.sitePath(SiteUrls.personalSpacePart());
  }

  private static personalSpacePart(): string {
    return '/personalSpace';
  }

  // Workbook Acceleration
  public workbookAccelerationsUrl(workbookId: string): string {
    return this.fullUrl(this.workbookAccelerationsPath(workbookId));
  }
  public workbookAccelerationsPath(workbookId: string): string {
    return this.sitePath(SiteUrls.workbookAccelerationsPart(workbookId));
  }
  private static workbookAccelerationsPart(workbookId: string): string {
    return `${SiteUrls.workbookPart(
      workbookId
    )}${SiteUrls.dataAccelerationPart()}`;
  }

  // Starters
  public startersUrl(): string {
    return this.fullUrl(this.startersPath());
  }
  public startersPath(): string {
    return this.sitePath(SiteUrls.startersPart());
  }
  private static startersPart(): string {
    return '/starters';
  }

  public startersByConnectionTypeUrl(connectionType: string): string {
    return this.fullUrl(this.startersByConnectionTypePath(connectionType));
  }
  public startersByConnectionTypePath(connectionType: string): string {
    return this.sitePath(SiteUrls.startersByConnectionTypePart(connectionType));
  }
  private static startersByConnectionTypePart(connectionType: string): string {
    return `${SiteUrls.startersPart()}/${connectionType}`;
  }

  // Connected Applications
  // TODO: Remove this path once references downstream are removed
  public connectedApplicationUrl(connectedApplicationId: string): string {
    return this.fullUrl(this.connectedApplicationPath(connectedApplicationId));
  }
  public connectedApplicationPath(connectedApplicationId: string): string {
    return this.sitePath(SiteUrls.connectedApplicationPart(connectedApplicationId));
  }
  private static connectedApplicationPart(connectedApplicationId: string): string {
    return `${SiteUrls.connectedApplicationsPart()}/${connectedApplicationId}`;
  }

  public connectedApplicationDirectTrustUrl(connectedApplicationId: string): string {
    return this.fullUrl(this.connectedApplicationDirectTrustPath(connectedApplicationId));
  }
  public connectedApplicationDirectTrustPath(connectedApplicationId: string): string {
    return this.sitePath(SiteUrls.connectedApplicationDirectTrustPart(connectedApplicationId));
  }
  public static connectedApplicationDirectTrustPart(connectedApplicationId: string): string {
    return `${SiteUrls.connectedApplicationsPart()}${SiteUrls.directTrustPart()}/${connectedApplicationId}`;
  }

  public connectedApplicationEasUrl(connectedApplicationId: string): string {
    return this.fullUrl(this.connectedApplicationEasPath(connectedApplicationId));
  }
  public connectedApplicationEasPath(connectedApplicationId: string): string {
    return this.sitePath(SiteUrls.connectedApplicationEasPart(connectedApplicationId));
  }
  public static connectedApplicationEasPart(connectedApplicationId: string): string {
    return `${SiteUrls.connectedApplicationsPart()}${SiteUrls.externalAuthorizationServersPart()}/${connectedApplicationId}`;
  }

  // Predictive Model

  public predictiveModelsUrl(): string {
    return this.fullUrl(this.predictiveModelsPath());
  }

  public predictiveModelsPath(): string {
    return this.sitePath(SiteUrls.predictiveModelsPart());
  }

  public static predictiveModelsPart(): string {
    return '/predictiveModels';
  }

  public predictiveModelUrl(id: string): string {
    return this.fullUrl(this.predictiveModelPath(id));
  }

  public predictiveModelPath(id: string): string {
    return this.sitePath(SiteUrls.predictiveModelPart(id));
  }

  public static predictiveModelPart(id: string): string {
    return `${SiteUrls.predictiveModelsPart()}/${id}`;
  }

  public predictiveModelOverviewUrl(id: string): string {
    return this.fullUrl(this.predictiveModelOverviewPath(id));
  }

  public predictiveModelOverviewPath(id: string): string {
    return this.sitePath(SiteUrls.predictiveModelOverviewPart(id));
  }

  public static predictiveModelOverviewPart(id: string): string {
    return `${SiteUrls.predictiveModelPart(id)}${SiteUrls.overviewPart()}`;
  }

  public predictiveModelConnectionsUrl(id: string): string {
    return this.fullUrl(this.predictiveModelConnectionsPath(id));
  }

  public predictiveModelConnectionsPath(id: string): string {
    return this.sitePath(SiteUrls.predictiveModelConnectionsPart(id));
  }

  public static predictiveModelConnectionsPart(id: string): string {
    return `${SiteUrls.predictiveModelPart(id)}${SiteUrls.connectionsPart()}`;
  }

  public predictiveModelConnectedContentUrl(id: string): string {
    return this.fullUrl(this.predictiveModelConnectedContentPath(id));
  }

  public predictiveModelConnectedContentPath(id: string): string {
    return this.sitePath(SiteUrls.predictiveModelConnectedContentPart(id));
  }

  public static predictiveModelConnectedContentPart(id: string): string {
    return `${SiteUrls.predictiveModelPart(id)}${SiteUrls.connectedContentPart()}`;
  }

  private static connectedContentPart(): string {
    return '/connectedContent';
  }

  // Predictive Model Editor
  public predictiveModelEditorUrl(): string {
    return this.fullUrl(this.predictiveModelEditorPath());
  }

  public predictiveModelEditorPath(): string {
    return this.sitePath(SiteUrls.predictiveModelEditorPart());
  }

  private static predictiveModelEditorPart(): string {
    return '/predictive-model-editor';
  }

  // Request Access
  public collectionRequestAccessUrl(contentIdentifier: string): string {
    return this.fullUrl(this.collectionRequestAccessPath(contentIdentifier));
  }
  public collectionRequestAccessPath(contentIdentifier: string): string {
    return this.sitePath(SiteUrls.collectionRequestAccessPart(contentIdentifier));
  }
  private static collectionRequestAccessPart(contentIdentifier: string): string {
    return `${SiteUrls.requestAccessPart()}${this.collectionPart(contentIdentifier)}`;
  }

  public datasourceRequestAccessUrl(contentIdentifier: string): string {
    return this.fullUrl(this.datasourceRequestAccessPath(contentIdentifier));
  }
  public datasourceRequestAccessPath(contentIdentifier: string): string {
    return this.sitePath(SiteUrls.datasourceRequestAccessPart(contentIdentifier));
  }
  private static datasourceRequestAccessPart(contentIdentifier: string): string {
    return `${SiteUrls.requestAccessPart()}${this.datasourcePart(contentIdentifier)}`;
  }

  public flowRequestAccessUrl(contentIdentifier: string): string {
    return this.fullUrl(this.flowRequestAccessPath(contentIdentifier));
  }
  public flowRequestAccessPath(contentIdentifier: string): string {
    return this.sitePath(SiteUrls.flowRequestAccessPart(contentIdentifier));
  }
  private static flowRequestAccessPart(contentIdentifier: string): string {
    return `${SiteUrls.requestAccessPart()}${this.flowPart(contentIdentifier)}`;
  }

  public metricRequestAccessUrl(contentIdentifier: string): string {
    return this.fullUrl(this.metricRequestAccessPath(contentIdentifier));
  }
  public metricRequestAccessPath(contentIdentifier: string): string {
    return this.sitePath(SiteUrls.metricRequestAccessPart(contentIdentifier));
  }
  private static metricRequestAccessPart(contentIdentifier: string): string {
    return `${SiteUrls.requestAccessPart()}${this.metricPart(contentIdentifier)}`;
  }

  public projectRequestAccessUrl(contentIdentifier: string): string {
    return this.fullUrl(this.projectRequestAccessPath(contentIdentifier));
  }
  public projectRequestAccessPath(contentIdentifier: string): string {
    return this.sitePath(SiteUrls.projectRequestAccessPart(contentIdentifier));
  }
  private static projectRequestAccessPart(contentIdentifier: string): string {
    return `${SiteUrls.requestAccessPart()}${this.projectPart(contentIdentifier)}`;
  }

  public viewRequestAccessUrl(contentIdentifier: string): string {
    return this.fullUrl(this.viewRequestAccessPath(contentIdentifier));
  }
  public viewRequestAccessPath(contentIdentifier: string): string {
    return this.sitePath(SiteUrls.viewRequestAccessPart(contentIdentifier));
  }
  private static viewRequestAccessPart(contentIdentifier: string): string {
    return `${SiteUrls.requestAccessPart()}${this.viewPart(contentIdentifier)}`;
  }

  public workbookRequestAccessUrl(contentIdentifier: string): string {
    return this.fullUrl(this.workbookRequestAccessPath(contentIdentifier));
  }
  public workbookRequestAccessPath(contentIdentifier: string): string {
    return this.sitePath(SiteUrls.workbookRequestAccessPart(contentIdentifier));
  }
  private static workbookRequestAccessPart(contentIdentifier: string): string {
    return `${SiteUrls.requestAccessPart()}${this.workbookPart(contentIdentifier)}`;
  }

  public virtualConnectionRequestAccessUrl(contentIdentifier: string): string {
    return this.fullUrl(this.virtualConnectionRequestAccessPath(contentIdentifier));
  }
  public virtualConnectionRequestAccessPath(contentIdentifier: string): string {
    return this.sitePath(SiteUrls.virtualConnectionRequestAccessPart(contentIdentifier));
  }
  private static virtualConnectionRequestAccessPart(contentIdentifier: string): string {
    return `${SiteUrls.requestAccessPart()}${this.virtualConnectionPart(contentIdentifier)}`;
  }

  public virtualConnectionTableRequestAccessUrl(contentIdentifier: string): string {
    return this.fullUrl(this.virtualConnectionTableRequestAccessPath(contentIdentifier));
  }
  public virtualConnectionTableRequestAccessPath(contentIdentifier: string): string {
    return this.sitePath(SiteUrls.virtualConnectionTableRequestAccessPart(contentIdentifier));
  }
  private static virtualConnectionTableRequestAccessPart(contentIdentifier: string): string {
    return `${SiteUrls.requestAccessPart()}${this.virtualConnectionTablePart(contentIdentifier)}`;
  }

  private static requestAccessPart(): string {
    return '/requestAccess';
  }

  // Grant Access
  public workbookGrantAccessUrl(contentIdentifier: string): string {
    return this.fullUrl(this.workbookGrantAccessPath(contentIdentifier));
  }
  public workbookGrantAccessPath(contentIdentifier: string): string {
    return this.sitePath(SiteUrls.workbookGrantAccessPart(contentIdentifier));
  }
  private static workbookGrantAccessPart(contentIdentifier: string): string {
    return `${SiteUrls.workbookPart(contentIdentifier)}${SiteUrls.viewsPart()}${SiteUrls.grantAccessPart()}`;
  }

  public viewGrantAccessUrl(workbookName: string, viewName: string): string {
    return this.fullUrl(this.viewGrantAccessPath(workbookName, viewName));
  }
  public viewGrantAccessPath(workbookName: string, viewName: string): string {
    return this.sitePath(SiteUrls.viewGrantAccessPart(workbookName, viewName));
  }
  private static viewGrantAccessPart(workbookName: string, viewName: string): string {
    return `${SiteUrls.viewPart(`${workbookName}/${viewName}`)}${SiteUrls.grantAccessPart()}`;
  }

  public projectGrantAccessUrl(contentIdentifier: string): string {
    return this.fullUrl(this.projectGrantAccessPath(contentIdentifier));
  }
  public projectGrantAccessPath(contentIdentifier: string): string {
    return this.sitePath(SiteUrls.projectGrantAccessPart(contentIdentifier));
  }
  private static projectGrantAccessPart(contentIdentifier: string): string {
    return `${SiteUrls.projectPart(contentIdentifier)}${SiteUrls.grantAccessPart()}`;
  }

  public metricGrantAccessUrl(contentIdentifier: string): string {
    return this.fullUrl(this.metricGrantAccessPath(contentIdentifier));
  }
  public metricGrantAccessPath(contentIdentifier: string): string {
    return this.sitePath(SiteUrls.metricGrantAccessPart(contentIdentifier));
  }
  private static metricGrantAccessPart(contentIdentifier: string): string {
    return `${SiteUrls.metricPart(contentIdentifier)}${SiteUrls.grantAccessPart()}`;
  }

  public flowGrantAccessUrl(contentIdentifier: string): string {
    return this.fullUrl(this.flowGrantAccessPath(contentIdentifier));
  }
  public flowGrantAccessPath(contentIdentifier: string): string {
    return this.sitePath(SiteUrls.flowGrantAccessPart(contentIdentifier));
  }
  private static flowGrantAccessPart(contentIdentifier: string): string {
    return `${SiteUrls.flowPart(contentIdentifier)}${SiteUrls.grantAccessPart()}`;
  }

  public datasourceGrantAccessUrl(contentIdentifier: string): string {
    return this.fullUrl(this.datasourceGrantAccessPath(contentIdentifier));
  }
  public datasourceGrantAccessPath(contentIdentifier: string): string {
    return this.sitePath(SiteUrls.datasourceGrantAccessPart(contentIdentifier));
  }
  private static datasourceGrantAccessPart(contentIdentifier: string): string {
    return `${SiteUrls.datasourcePart(contentIdentifier)}${SiteUrls.dataConnectionPart()}${SiteUrls.grantAccessPart()}`;
  }

  public collectionGrantAccessUrl(contentIdentifier: string): string {
    return this.fullUrl(this.collectionGrantAccessPath(contentIdentifier));
  }
  public collectionGrantAccessPath(contentIdentifier: string): string {
    return this.sitePath(SiteUrls.collectionGrantAccessPart(contentIdentifier));
  }
  private static collectionGrantAccessPart(contentIdentifier: string): string {
    return `${SiteUrls.collectionPart(contentIdentifier)}${SiteUrls.grantAccessPart()}`;
  }

  public virtualConnectionGrantAccessUrl(contentIdentifier: string): string {
    return this.fullUrl(this.virtualConnectionGrantAccessPath(contentIdentifier));
  }
  public virtualConnectionGrantAccessPath(contentIdentifier: string): string {
    return this.sitePath(SiteUrls.virtualConnectionGrantAccessPart(contentIdentifier));
  }
  private static virtualConnectionGrantAccessPart(contentIdentifier: string): string {
    return `${SiteUrls.virtualConnectionPart(contentIdentifier)}${SiteUrls.grantAccessPart()}`;
  }

  private static grantAccessPart(): string {
    return '/grantAccess';
  }

  // NTBUE (new tableau business user experience) URL, initial setup for the steal thread demo
  public ntbuePath(): string {
    return this.sitePath(SiteUrls.ntbuePart());
  }
  private static ntbuePart(): string {
    return '/ntbue/';
  }

  // This code was copied from VizPortal Client and is consumed by at least one other repository.
  // This isn't logically the right place for this sort of logic, but there isn't currently a better place.
  private generateUniqueAuxiliaryId(): string {
    const base36Digit = () => {
      const s = '0123456789abcdefghijklmnopqrstuvwxyz';
      return s[Math.floor(Math.random() * s.length)];
    };

    const b36 = (len: number) => {
      let s = '';
      for (let i = 0; i < len; ++i) {
        s += base36Digit();
      }
      return s;
    };

    // To generate the auxiliary ID, use a base-32 encoding of the client time-stamp plus a guid-like base-36 pseudo-unique identifier.
    // We use half the number of digits compared to a standard GUID since base-36 has over twice the number of encoding bits.
    // A collision of identifiers generated in this way is virtually impossible.
    return (
      new Date().getTime().toString(32).toLowerCase() +
      '$' +
      b36(4) +
      '-' +
      b36(2) +
      '-' +
      b36(2) +
      '-' +
      b36(2) +
      '-' +
      b36(6)
    );
  }
}
